import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Container,
  Form,
  FormGroup,
  Label,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import { Doughnut, HorizontalBar, Bar } from "react-chartjs-2";
import jsPDF from "jspdf";
import DateTime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import AsyncSelect from "react-select/async";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

import ReactSelect from "components/Form/Select/Select";
import useForm from "lib/useForm";
import Loading from "lib/loading";
import rotas from "services/rotas";
import rest from "lib/rest";

const formSwal = withReactContent(Swal);

/* ─── Paleta de cores por classe ─── */
const CLASSE_STYLE = {
  A: { bg: "#ECFDF5", color: "#065f46", border: "1px solid #A7F3D0" },
  B: { bg: "#FFFBEB", color: "#92400e", border: "1px solid #FDE68A" },
  C: { bg: "#FEF2F2", color: "#991b1b", border: "1px solid #FECACA" },
  D: { bg: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" },
};

const SITUACAO_STYLE = {
  NORMAL:  { bg: "#f0f9ff", color: "#0369a1" },
  RUPTURA: { bg: "#FEF2F2", color: "#b91c1c" },
  EXCESSO: { bg: "#fef9c3", color: "#713f12" },
  ATENCAO: { bg: "#fff7ed", color: "#9a3412" },
  MORTO:   { bg: "#f9fafb", color: "#6b7280" },
};

const SITUACAO_LABEL = {
  NORMAL:  "NORMAL",
  RUPTURA: "RUPTURA",
  EXCESSO: "EXCESSO",
  ATENCAO: "ATENÇÃO",
  MORTO:   "MORTO",
};

const Badge = ({ text, style }) => (
  <span style={{
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: "9999px",
    fontSize: "11.5px",
    fontWeight: 700,
    background: style.bg,
    color: style.color,
    border: style.border || "none",
  }}>
    {text}
  </span>
);

const fmtNum = (v) =>
  new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2 }).format(v || 0);

const fmtInt = (v) => new Intl.NumberFormat("pt-BR").format(v || 0);

const fmtCnpj = (v) => {
  const s = String(v || "").replace(/\D/g, "");
  if (s.length !== 14) return v || "";
  return `${s.slice(0,2)}.${s.slice(2,5)}.${s.slice(5,8)}/${s.slice(8,12)}-${s.slice(12)}`;
};

const PERC_ABC_DEFAULT = { A: 80, B: 15, C: 5 };
const PERC_ABC_KEY     = "wg_curvaAbcd_percABC";

/* Reclassifica um item com base nos limiares configurados.
   Classe D (sem giro) nunca muda. */
const _reclassItem = (item, pA, pAB) => {
  if (item.curva_classe === "D") return item;
  const p = item.perc_acum != null ? Number(item.perc_acum) : null;
  if (p === null) return item;
  const nova = p <= pA ? "A" : p <= pAB ? "B" : "C";
  return nova !== item.curva_classe ? { ...item, curva_classe: nova } : item;
};

const coberturaTxt = (dias) =>
  dias >= 9999 ? "∞" : `${fmtInt(dias)} dias`;

/* Datas padrão: hoje e 90 dias atrás (formato DD/MM/YYYY) */
const _pad = (n) => String(n).padStart(2, "0");
const _hoje    = new Date();
const _noventa = new Date(_hoje);
_noventa.setDate(_noventa.getDate() - 90);
const DATA_PADRAO_I = `${_pad(_noventa.getDate())}/${_pad(_noventa.getMonth() + 1)}/${_noventa.getFullYear()}`;
const DATA_PADRAO_F = `${_pad(_hoje.getDate())}/${_pad(_hoje.getMonth() + 1)}/${_hoje.getFullYear()}`;

const CurvaABCD = () => {
  const [form, , , onChangeSelect, onChangeDate] = useForm({ inicio: DATA_PADRAO_I, final: DATA_PADRAO_F });

  const [loading, setLoading]           = useState(true);
  const [loadingTabela, setLoadingTabela] = useState(false);
  const [lojas, setLojas]               = useState([]);
  const [categorias, setCategorias]     = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [listaCurva, setListaCurva]     = useState([]);
  const [resumo, setResumo]             = useState(null);

  /* filtros locais (client-side) */
  const [filtroClasse, setFiltroClasse]     = useState("");
  const [filtroSituacao, setFiltroSituacao] = useState("");
  const [filtroLoja, setFiltroLoja]         = useState("");
  const [filtroTexto, setFiltroTexto]       = useState("");
  const [activeTab, setActiveTab]           = useState("1");

  /* controle do select de lojas (para permitir "Selecionar todas") */
  const [selectedLojas, setSelectedLojas] = useState(null);

  /* ── Configuração percentuais ABC ── */
  const [percABC, setPercABC] = useState(() => {
    try {
      const s = localStorage.getItem(PERC_ABC_KEY);
      if (s) return JSON.parse(s);
    } catch {}
    return PERC_ABC_DEFAULT;
  });
  const [percTemp, setPercTemp]       = useState(PERC_ABC_DEFAULT);
  const [showConfigABC, setShowConfigABC] = useState(false);
  const configRef = useRef(null);
  const percCustom = percABC.A !== PERC_ABC_DEFAULT.A || percABC.B !== PERC_ABC_DEFAULT.B;

  /* ── Por Produto ── */
  const [formPorProduto, setFormPorProduto] = useState({ inicio: DATA_PADRAO_I, final: DATA_PADRAO_F });
  const [produtoPorLojaSelect, setProdutoPorLojaSelect] = useState(null);
  const [dadosPorLoja, setDadosPorLoja]   = useState([]);
  const [loadingPorLoja, setLoadingPorLoja] = useState(false);

  /* ── Modal de exportação ── */
  const [modalExportar, setModalExportar] = useState(false);
  const [opcoesExportar, setOpcoesExportar] = useState({
    consolidado: true,
    classeA: true, classeB: true, classeC: true, classeD: true,
    porLoja: true,
    transferencias: false,
    priAlta: true, priMedia: true, priBaixa: true,
    formato: "pdf",
  });

  /* ── Carregamentos iniciais ── */
  const listarLojas = () => {
    rest.get(rotas.lojas).then((ret) => {
      if (ret.success === "S" && ret.lista !== "") {
        let lista = [];
        ret.lista.forEach((l) => {
          lista.push({ id: l.filial_id, title: l.fantasia });
        });
        setLojas(lista);
      }
      setLoading(false);
    });
  };

  const listarCategorias = () => {
    rest.get(rotas.categoriaSelect).then((ret) => {
      if (ret.success === "S" && ret.lista !== "" && ret.lista !== null) {
        let lista = [];
        ret.lista.forEach((lst) => {
          lista.push({ id: lst.categoria_id, title: lst.categoria });
        });
        setCategorias(lista);
      }
    });
  };

  const listarFornecedores = () => {
    rest.get(rotas.fornecedorProduto).then((ret) => {
      if (ret.success === "S" && ret.lista !== "" && ret.lista !== null) {
        let lista = [];
        ret.lista.forEach((lst) => {
          lista.push({ id: lst.fornecedor_id, title: lst.fornecedor });
        });
        setFornecedores(lista);
      }
    });
  };

  useEffect(() => {
    listarLojas();
    listarCategorias();
    listarFornecedores();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Fecha o popover de config ao clicar fora dele */
  useEffect(() => {
    if (!showConfigABC) return;
    const handler = (e) => {
      if (configRef.current && !configRef.current.contains(e.target)) {
        setShowConfigABC(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showConfigABC]);

  /* Contagens A/B/C/D recalculadas com os percentuais configurados */
  const contagensABC = useMemo(() => {
    if (!listaCurva.length) return null;
    const pA = percABC.A, pAB = percABC.A + percABC.B;
    const c = { A: 0, B: 0, C: 0, D: 0 };
    listaCurva.forEach((i) => { c[_reclassItem(i, pA, pAB).curva_classe]++; });
    return c;
  }, [listaCurva, percABC]);

  /* ── AsyncSelect de produtos ── */
  const buscaProdutos = (inputValue, callback) => {
    if (!inputValue || inputValue.length < 3) return callback([]);
    rest
      .get(rotas.filtroProdutoRelatorio + "?produto=" + inputValue)
      .then((ret) => {
        if (ret.success === "S" && ret.lista) {
          callback(
            ret.lista.map((p) => ({
              value:      p.produto_id,
              label:      p.produto,
              cod_barras: p.cod_barras || '',
            }))
          );
        } else {
          callback([]);
        }
      });
  };

  /* ── Submit da pesquisa ── */
  const pesquisar = () => {
    if (!form.filialSelect || !form.filialSelect.length) {
      formSwal.fire("Atenção", "Selecione pelo menos uma loja.", "warning");
      return;
    }

    setLoadingTabela(true);
    setFiltroClasse("");
    setFiltroSituacao("");
    setFiltroLoja("");
    /* Mantém dados anteriores visíveis durante o carregamento */

    rest.post(rotas.curvaAbcd, { form }).then((ret) => {
      setLoadingTabela(false);
      if (ret.success === "S") {
        setListaCurva(ret.lista || []);
        setResumo(ret.resumo || null);
      } else {
        setListaCurva([]);
        setResumo(null);
        formSwal.fire("Erro", ret.msg || "Erro ao processar a curva ABCD.", "error");
      }
    });
  };

  /* ── Pesquisa por produto em todas as lojas ── */
  const pesquisarPorProduto = () => {
    if (!produtoPorLojaSelect) {
      formSwal.fire("Atenção", "Selecione um produto para analisar.", "warning");
      return;
    }
    if (!lojas.length) {
      formSwal.fire("Atenção", "Nenhuma loja disponível.", "warning");
      return;
    }
    const todasLojas = lojas.map((l) => ({ id: l.id }));
    const payload = {
      form: {
        inicio: formPorProduto.inicio,
        final: formPorProduto.final,
        filialSelect: todasLojas,
        produtoSelect: [produtoPorLojaSelect],
      },
    };
    setLoadingPorLoja(true);
    setDadosPorLoja([]);
    rest.post(rotas.curvaAbcd, payload).then((ret) => {
      setLoadingPorLoja(false);
      if (ret.success === "S") {
        setDadosPorLoja(ret.lista || []);
      } else {
        formSwal.fire("Erro", ret.msg || "Erro ao buscar dados.", "error");
      }
    });
  };

  /* ── Helpers de exportação ── */
  const _prepararDados = (opcoes) => {
    const cls = ["A","B","C","D"].filter((c) => opcoes[`classe${c}`]);
    const consolidado = opcoes.consolidado
      ? listaFiltrada.filter((i) => cls.includes(i.curva_classe))
      : [];
    const loja = opcoes.porLoja ? resumoPorLoja : [];
    const prios = [
      opcoes.priAlta  && "ALTA",
      opcoes.priMedia && "MEDIA",
      opcoes.priBaixa && "BAIXA",
    ].filter(Boolean);
    const transf =
      opcoes.transferencias && lojasNaLista.length > 1
        ? sugestoesTransferencia.filter((s) => prios.includes(s.prioridade))
        : [];
    return { consolidado, loja, transf, cls };
  };

  const _downloadCSV = (csv, nome) => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href  = URL.createObjectURL(blob);
    link.download = `${nome}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ── Exportação Excel (CSV UTF-8) ── */
  const exportarExcel = (opcoes) => {
    const { consolidado, loja, transf, cls } = _prepararDados(opcoes);
    if (!consolidado.length && !loja.length && !transf.length) {
      formSwal.fire("Aviso", "Nenhum dado para exportar com as opcoes selecionadas.", "warning");
      return;
    }

    const sep = ";";
    const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
    const linhas = [];

    /* Seção Consolidado */
    if (consolidado.length) {
      linhas.push(esc(`CONSOLIDADO - Classes: ${cls.join(", ")}`));
      linhas.push([
        "#","Loja","Produto","Fornecedor","Categoria",
        "Qtde Vendida","Vendas R$","% Acum","Classe",
        "Saldo","Estoque R$","Cobertura (dias)","Situacao",
      ].map(esc).join(sep));
      consolidado.forEach((i) => linhas.push([
        i.curva_classe === "D" ? "" : (i.curva_classificacao || ""),
        i.fantasia || "", i.produto || "", i.fornecedor || "", i.categoria || "",
        i.curva_qtvendida || 0,
        String(i.curva_totalvenda  || 0).replace(".", ","),
        i.perc_acum != null ? String(i.perc_acum).replace(".", ",") : "",
        i.curva_classe || "", i.prod_saldo || 0,
        String(i.estoque_valor || 0).replace(".", ","),
        i.cobertura_dias >= 9999 ? "" : Math.max(0, i.cobertura_dias || 0),
        SITUACAO_LABEL[i.situacao] || i.situacao || "",
      ].map(esc).join(sep)));
      linhas.push("");
    }

    /* Seção Por Loja */
    if (loja.length) {
      linhas.push(esc("RESUMO POR LOJA"));
      linhas.push(["Loja","A","B","C","D (Parado)","Ruptura","Excesso",
        "Vendas (R$)","Capital Morto (R$)"].map(esc).join(sep));
      loja.forEach((l) => linhas.push([
        l.fantasia || "", l.A, l.B, l.C, l.D, l.ruptura, l.excesso,
        String(l.total_vendas  || 0).replace(".", ","),
        String(l.capital_morto || 0).replace(".", ","),
      ].map(esc).join(sep)));
      linhas.push("");
    }

    /* Seção Transferências */
    if (transf.length) {
      linhas.push(esc("SUGESTOES DE TRANSFERENCIA"));
      linhas.push(["Produto","Categoria","Classe","Prioridade",
        "De (doadora)","Saldo Doadora","Cobert. Doadora",
        "Para (deficitaria)","Saldo Deficitaria","Cobert. Deficitaria",
        "Qtd. Sugerida","Valor Estimado (R$)"].map(esc).join(sep));
      transf.forEach((s) => linhas.push([
        s.produto || "", s.categoria || "", s.classe || "", s.prioridade || "",
        s.doadora.fantasia || "", s.doadora.saldo,
        s.doadora.cobertura_dias >= 9999 ? "" : Math.max(0, s.doadora.cobertura_dias),
        s.deficitaria.fantasia || "", s.deficitaria.saldo,
        s.deficitaria.cobertura_dias >= 9999 ? "" : Math.max(0, s.deficitaria.cobertura_dias),
        s.qtd_sugerida,
        String(s.valor_estimado || 0).replace(".", ","),
      ].map(esc).join(sep)));
    }

    _downloadCSV("﻿" + linhas.join("\r\n"), "curva_abcd");
  };

  /* ── Exportação PDF (jsPDF landscape A4) ── */
  const exportarPDF = (opcoes) => {
    const { consolidado, loja, transf, cls } = _prepararDados(opcoes);
    if (!consolidado.length && !loja.length && !transf.length) {
      formSwal.fire("Aviso", "Nenhum dado para exportar com as opcoes selecionadas.", "warning");
      return;
    }

    // eslint-disable-next-line new-cap
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const PW  = doc.internal.pageSize.getWidth();
    const PH  = doc.internal.pageSize.getHeight();
    const ML  = 10, MR = 10, UW = PW - ML - MR;
    const ROW_H = 8, HEAD_H = 6, FONT_SM = 5.5, FONT_TH = 6;

    const trunc = (s, maxMm, fSize) => {
      const max = Math.floor(maxMm / (fSize * 0.194));
      const str = String(s ?? "");
      return str.length > max ? str.substring(0, max - 1) + "." : str;
    };

    const addFooter = () => {
      doc.setFontSize(7);
      doc.setTextColor(180, 180, 180);
      doc.text(`Pagina ${doc.internal.getCurrentPageInfo().pageNumber}`, PW - MR, PH - 4, { align: "right" });
      doc.text("WebGestor Digifarma", ML, PH - 4);
    };

    /* Desenha tabela genérica; retorna y final */
    const drawTable = (cols, rows, yStart) => {
      let y = yStart;

      const drawRow = (cells, isHdr, yp) => {
        let x = ML;
        doc.setFontSize(isHdr ? FONT_TH : FONT_SM);
        doc.setFont("helvetica", isHdr ? "bold" : "normal");
        doc.setTextColor(60, 60, 60);
        cols.forEach(([, w, align], idx) => {
          const cell  = cells[idx];
          const isObj = !isHdr && cell !== null && typeof cell === "object" && "main" in cell;
          const main  = isObj ? cell.main : cell;
          const sub   = isObj ? cell.sub  : null;
          const txt   = trunc(String(main ?? ""), w - 1, isHdr ? FONT_TH : FONT_SM);
          const tx    = align === "right"  ? x + w - 1
                      : align === "center" ? x + w / 2 : x + 1;
          doc.setFontSize(isHdr ? FONT_TH : FONT_SM);
          doc.setTextColor(isHdr ? 44 : 60, isHdr ? 62 : 60, isHdr ? 80 : 60);
          doc.text(txt, tx, yp, { align });
          if (sub) {
            const subtxt = trunc(String(sub), w - 1, 4.5);
            doc.setFontSize(4.5);
            doc.setTextColor(150, 150, 150);
            doc.text(subtxt, tx, yp + 3, { align });
          }
          x += w;
        });
        doc.setTextColor(60, 60, 60);
      };

      const drawHeader = (yp) => {
        doc.setFillColor(240, 240, 240);
        doc.rect(ML, yp - HEAD_H + 1, UW, HEAD_H, "F");
        doc.setTextColor(44, 62, 80);
        drawRow(cols.map(([h]) => h), true, yp);
        return yp + ROW_H + 1;
      };

      y = drawHeader(y);
      doc.setTextColor(60, 60, 60);

      rows.forEach((cells, idx) => {
        if (y + ROW_H > PH - 8) {
          addFooter();
          doc.addPage();
          y = 12;
          y = drawHeader(y);
          doc.setTextColor(60, 60, 60);
        }
        if (idx % 2 === 0) {
          doc.setFillColor(250, 250, 252);
          doc.rect(ML, y - ROW_H + 1, UW, ROW_H, "F");
        }
        drawRow(cells, false, y);
        y += ROW_H;
      });
      return y;
    };

    /* Título de seção: faixa colorida */
    const drawSectionTitle = (title, y) => {
      if (y + 16 > PH - 8) { addFooter(); doc.addPage(); y = 12; }
      doc.setFillColor(44, 62, 80);
      doc.rect(ML, y, UW, 9, "F");   /* altura 9mm: 2mm topo + texto + 2mm base */
      doc.setFontSize(7.5);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text(title, ML + 3, y + 6); /* baseline a 6mm do topo da faixa */
      return y + 12;
    };

    /* ── Cabeçalho do documento ── */
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(44, 62, 80);
    doc.text("Curva ABCD - Analise de Estoque", ML, 12);

    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(127, 140, 141);
    const infoLine = resumo
      ? `Periodo: ${resumo.dias_periodo} dias - Total vendas: R$ ${resumo.total_vendas} - ${listaCurva.length} produtos`
      : "";
    doc.text(`Gerado em: ${new Date().toLocaleString("pt-BR")}   ${infoLine}`, ML, 18);
    doc.setDrawColor(231, 76, 60);
    doc.setLineWidth(0.4);
    doc.line(ML, 22, PW - MR, 22);
    let y = 27;

    /* ══ SEÇÃO 1: CONSOLIDADO ══ */
    if (consolidado.length) {
      y = drawSectionTitle(`CONSOLIDADO (${consolidado.length} produtos) - Classes: ${cls.join(", ")}`, y);

      y = drawTable([
        ["#",          10, "center"],
        ["Loja",       28, "left"  ],
        ["Produto",    65, "left"  ],
        ["Cat.",       20, "left"  ],
        ["Qtde",       14, "right" ],
        ["Vendas R$",  28, "right" ],
        ["% Acum",     14, "right" ],
        ["Classe",     14, "center"],
        ["Saldo",      14, "right" ],
        ["Estoque R$", 28, "right" ],
        ["Cobert.",    14, "right" ],
        ["Situacao",   28, "center"],
      ], consolidado.map((i) => [
        i.curva_classe === "D" ? "-" : (i.curva_classificacao || ""),
        { main: i.fantasia || "", sub: fmtCnpj(i.cnpj) },
        { main: i.produto || "", sub: i.cod_barras || "" }, i.categoria || "",
        fmtInt(i.curva_qtvendida), fmtNum(i.curva_totalvenda),
        i.perc_acum != null ? i.perc_acum + "%" : "-",
        i.curva_classe || "", fmtInt(i.prod_saldo), fmtNum(i.estoque_valor),
        i.cobertura_dias >= 9999 ? "s/giro" : Math.max(0, i.cobertura_dias || 0) + "d",
        SITUACAO_LABEL[i.situacao] || i.situacao || "",
      ]), y);
      y += 6;
    }

    /* ══ SEÇÃO 2: POR LOJA ══ */
    if (loja.length) {
      y = drawSectionTitle(`RESUMO POR LOJA (${loja.length} ${loja.length === 1 ? "filial" : "filiais"})`, y);

      y = drawTable([
        ["Loja",            66, "left" ],
        ["A",               22, "right"],
        ["B",               22, "right"],
        ["C",               22, "right"],
        ["D (Parado)",      28, "right"],
        ["Ruptura",         24, "right"],
        ["Excesso",         24, "right"],
        ["Vendas (R$)",     35, "right"],
        ["Cap. Morto (R$)", 34, "right"],
      ], loja.map((l) => [
        { main: l.fantasia || "", sub: fmtCnpj(l.cnpj) }, l.A, l.B, l.C, l.D,
        l.ruptura, l.excesso,
        fmtNum(l.total_vendas), fmtNum(l.capital_morto),
      ]), y);
      y += 6;
    }

    /* ══ SEÇÃO 3: TRANSFERÊNCIAS ══ */
    if (transf.length) {
      y = drawSectionTitle(`SUGESTÕES DE TRANSFERÊNCIA (${transf.length} ${transf.length === 1 ? "sugestão" : "sugestões"})`, y);

      y = drawTable([
        ["Produto",            62, "left"  ],
        ["Classe",             14, "center"],
        ["Prioridade",         22, "center"],
        ["De (doadora)",       47, "left"  ],
        ["Para (deficitaria)", 47, "left"  ],
        ["Qtd.",               20, "right" ],
        ["Valor Est.",         35, "right" ],
        ["Situacao Dest.",     30, "right" ],
      ], transf.map((s) => [
        { main: s.produto || "", sub: s.cod_barras || "" }, s.classe || "",
        { ALTA:"Alta", MEDIA:"Media", BAIXA:"Baixa" }[s.prioridade] || s.prioridade,
        { main: s.doadora.fantasia || "", sub: fmtCnpj(s.doadora.cnpj) },
        { main: s.deficitaria.fantasia || "", sub: fmtCnpj(s.deficitaria.cnpj) },
        fmtInt(s.qtd_sugerida),
        s.valor_estimado > 0 ? fmtNum(s.valor_estimado) : "-",
        SITUACAO_LABEL[s.deficitaria.situacao] || s.deficitaria.situacao || "",
      ]), y);
    }

    addFooter();
    doc.save(`curva_abcd_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  /* ── Exportação CMV (CSV para importação de custo) ── */
  const exportarCMV = (opcoes) => {
    const { consolidado } = _prepararDados(opcoes);
    const lista = consolidado.length ? consolidado : listaFiltrada;
    if (!lista.length) return;

    const sep = ";";
    const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;

    const cab = [
      "cod_barras","produto_id","produto","categoria","fornecedor",
      "filial_id","loja","classe","custo_atual","valor_ult_compra",
      "saldo_estoque","cobertura_dias",
    ].map(esc).join(sep);

    const linhas = lista.map((i) => [
      i.cod_barras || "",
      i.produto_id || "",
      i.produto    || "",
      i.categoria  || "",
      i.fornecedor || "",
      i.filial     || "",
      i.fantasia   || "",
      i.curva_classe || "",
      String(i.prod_cmv          || 0).replace(".", ","),
      String(i.valor_ult_compra  || 0).replace(".", ","),
      i.prod_saldo || 0,
      i.cobertura_dias >= 9999 ? "" : Math.max(0, i.cobertura_dias || 0),
    ].map(esc).join(sep));

    _downloadCSV("﻿" + [cab, ...linhas].join("\r\n"), "cmv_import");
  };

  /* ── Disparar exportação a partir do modal ── */
  const gerarExportacao = () => {
    setModalExportar(false);
    if      (opcoesExportar.formato === "pdf")   exportarPDF(opcoesExportar);
    else if (opcoesExportar.formato === "excel") exportarExcel(opcoesExportar);
    else if (opcoesExportar.formato === "cmv")   exportarCMV(opcoesExportar);
  };

  /* ── Lista filtrada (client-side) ── */
  const CLASSE_ORD = { A: 1, B: 2, C: 3, D: 4 };
  const listaFiltrada = useMemo(() => {
    const pA = percABC.A, pAB = percABC.A + percABC.B;
    /* Aplica reclassificação antes de qualquer filtro */
    let lst = listaCurva.map((i) => _reclassItem(i, pA, pAB));
    if (filtroClasse) {
      if (filtroClasse === "RUPTURA") {
        lst = lst.filter((i) => i.situacao === "RUPTURA");
      } else if (filtroClasse === "EXCESSO") {
        lst = lst.filter((i) => i.situacao === "EXCESSO");
      } else {
        lst = lst.filter((i) => i.curva_classe === filtroClasse);
      }
    }
    if (filtroSituacao) {
      lst = lst.filter((i) => i.situacao === filtroSituacao);
    }
    if (filtroLoja) {
      lst = lst.filter((i) => String(i.filial) === filtroLoja);
    }
    if (filtroTexto) {
      const termo = filtroTexto.toLowerCase();
      lst = lst.filter(
        (i) =>
          (i.produto    || "").toLowerCase().includes(termo) ||
          (i.fornecedor || "").toLowerCase().includes(termo) ||
          (i.categoria  || "").toLowerCase().includes(termo) ||
          (i.cod_barras || "").toLowerCase().includes(termo)
      );
    }
    /* Ordenação estável: A→B→C→D, dentro de cada classe por rank */
    return [...lst].sort((a, b) => {
      const ca = CLASSE_ORD[a.curva_classe] || 5;
      const cb = CLASSE_ORD[b.curva_classe] || 5;
      if (ca !== cb) return ca - cb;
      return (a.curva_classificacao || 0) - (b.curva_classificacao || 0);
    });
  }, [listaCurva, filtroClasse, filtroSituacao, filtroLoja, filtroTexto, percABC]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Resumo por loja (tab Por Loja) ── */
  const resumoPorLoja = useMemo(() => {
    const mapa = {};
    listaCurva.forEach((item) => {
      const key = item.filial;
      if (!mapa[key]) {
        mapa[key] = {
          filial: item.filial,
          fantasia: item.fantasia,
          cnpj: item.cnpj || "",
          A: 0, B: 0, C: 0, D: 0,
          ruptura: 0,
          excesso: 0,
          total_vendas: 0,
          capital_morto: 0,
        };
      }
      mapa[key][item.curva_classe]++;
      mapa[key].total_vendas += item.curva_totalvenda || 0;
      if (item.situacao === "RUPTURA") mapa[key].ruptura++;
      if (item.situacao === "EXCESSO") mapa[key].excesso++;
      if (item.curva_classe === "D") mapa[key].capital_morto += item.estoque_valor || 0;
    });
    return Object.values(mapa);
  }, [listaCurva]);

  /* ─── Lojas únicas para dropdown de filtro ─── */
  const lojasNaLista = useMemo(() => {
    const map = {};
    listaCurva.forEach((i) => {
      if (!map[i.filial]) map[i.filial] = i.fantasia;
    });
    return Object.entries(map).map(([id, nome]) => ({ id, nome }));
  }, [listaCurva]);

  /* ─── Sugestões de Transferência entre Filiais ─── */
  const sugestoesTransferencia = useMemo(() => {
    if (!listaCurva.length || !resumo || lojasNaLista.length < 2) return [];

    const diasPeriodo = Number(resumo.dias_periodo) || 30;

    /* Agrupar itens pelo produto_id */
    const porProduto = {};
    listaCurva.forEach((item) => {
      const key = item.produto_id;
      if (!porProduto[key]) porProduto[key] = [];
      porProduto[key].push(item);
    });

    const sugestoes = [];

    Object.values(porProduto).forEach((itens) => {
      if (itens.length < 2) return; /* produto só em 1 loja */

      const deficitarias = itens.filter(
        (i) => i.situacao === "RUPTURA" || i.situacao === "ATENCAO"
      );
      const doadoras = itens.filter(
        (i) =>
          i.situacao === "EXCESSO" ||
          i.curva_classe === "D" ||
          i.cobertura_dias > 90
      );

      if (!deficitarias.length || !doadoras.length) return;

      deficitarias.forEach((def) => {
        /* vendas diárias da deficitária */
        const vdDef = diasPeriodo > 0 ? (def.curva_qtvendida || 0) / diasPeriodo : 0;
        /* quanto falta para cobrir 30 dias */
        const qtdNecessaria = Math.max(
          0,
          Math.ceil(30 * vdDef) - (def.prod_saldo || 0)
        );
        if (qtdNecessaria <= 0) return;

        doadoras.forEach((doa) => {
          if (doa.filial === def.filial) return; /* mesma loja */

          const vdDoa = diasPeriodo > 0 ? (doa.curva_qtvendida || 0) / diasPeriodo : 0;
          /* manter pelo menos 30d na doadora (ou 0 se classe D) */
          const estoqueSeguro = doa.curva_classe === "D" ? 0 : Math.ceil(30 * vdDoa);
          const disponivel = Math.max(0, (doa.prod_saldo || 0) - estoqueSeguro);
          if (disponivel <= 0) return;

          const qtdSugerida = Math.min(qtdNecessaria, disponivel);
          if (qtdSugerida <= 0) return;

          /* preço médio estimado para o valor da transferência */
          const precoMedio =
            def.curva_qtvendida > 0
              ? (def.curva_totalvenda || 0) / def.curva_qtvendida
              : doa.curva_qtvendida > 0
              ? (doa.curva_totalvenda || 0) / doa.curva_qtvendida
              : 0;

          const prioridade =
            def.curva_classe === "A" ? "ALTA"
            : def.curva_classe === "B" ? "MEDIA"
            : "BAIXA";

          sugestoes.push({
            _key: `${def.produto_id}_${def.filial}_${doa.filial}`,
            produto_id:    def.produto_id,
            produto:       def.produto,
            cod_barras:    def.cod_barras || doa.cod_barras || null,
            categoria:     def.categoria,
            classe:        def.curva_classe,
            prioridade,
            deficitaria: {
              filial:        def.filial,
              fantasia:      def.fantasia,
              cnpj:          def.cnpj || "",
              saldo:         def.prod_saldo || 0,
              cobertura_dias: def.cobertura_dias,
              situacao:      def.situacao,
              vd:            vdDef,
            },
            doadora: {
              filial:        doa.filial,
              fantasia:      doa.fantasia,
              cnpj:          doa.cnpj || "",
              saldo:         doa.prod_saldo || 0,
              cobertura_dias: doa.cobertura_dias,
              situacao:      doa.situacao,
              classe:        doa.curva_classe,
              disponivel,
            },
            qtd_sugerida:  qtdSugerida,
            valor_estimado: precoMedio * qtdSugerida,
          });
        });
      });
    });

    /* Ordenar: ALTA → MEDIA → BAIXA, depois por valor estimado desc */
    const PORD = { ALTA: 1, MEDIA: 2, BAIXA: 3 };
    return sugestoes.sort((a, b) => {
      if (PORD[a.prioridade] !== PORD[b.prioridade])
        return PORD[a.prioridade] - PORD[b.prioridade];
      return (b.valor_estimado || 0) - (a.valor_estimado || 0);
    });
  }, [listaCurva, resumo, lojasNaLista]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─── Dados calculados para os gráficos ─── */
  const COR_CLASSE   = { A: "#27ae60", B: "#D97706", C: "#DC2626", D: "#9ca3af" };
  const COR_SITUACAO = {
    NORMAL:  "#0369a1",
    RUPTURA: "#b91c1c",
    EXCESSO: "#D97706",
    ATENCAO: "#9a3412",
    MORTO:   "#9ca3af",
  };

  const dadosGraficos = useMemo(() => {
    if (!listaCurva.length) return null;

    const qtdClasse     = { A: 0, B: 0, C: 0, D: 0 };
    const vendasClasse  = { A: 0, B: 0, C: 0, D: 0 };
    const estoqueClasse = { A: 0, B: 0, C: 0, D: 0 };
    const qtdSituacao   = {};
    const vendasLoja    = {};
    const lojaStacked   = {};

    listaCurva.forEach((item) => {
      const cl = item.curva_classe;
      if (cl in qtdClasse) {
        qtdClasse[cl]++;
        vendasClasse[cl]  += item.curva_totalvenda || 0;
        estoqueClasse[cl] += item.estoque_valor    || 0;
      }
      const sit = item.situacao;
      qtdSituacao[sit] = (qtdSituacao[sit] || 0) + 1;

      const loja = item.fantasia;
      vendasLoja[loja] = (vendasLoja[loja] || 0) + (item.curva_totalvenda || 0);
      if (!lojaStacked[loja]) lojaStacked[loja] = { A: 0, B: 0, C: 0, D: 0 };
      if (cl in lojaStacked[loja]) lojaStacked[loja][cl]++;
    });

    /* Top 10 por valor de vendas */
    const top10 = [...listaCurva]
      .sort((a, b) => (b.curva_totalvenda || 0) - (a.curva_totalvenda || 0))
      .slice(0, 10);

    /* Pareto — top 50 para a curva ABC clássica */
    const pareto50 = [...listaCurva]
      .sort((a, b) => (b.curva_totalvenda || 0) - (a.curva_totalvenda || 0))
      .slice(0, 50);
    const totalVendas50 = pareto50.reduce((s, i) => s + (i.curva_totalvenda || 0), 0);
    let acum = 0;
    const paretoAcum = pareto50.map((i) => {
      acum += i.curva_totalvenda || 0;
      return totalVendas50 > 0
        ? parseFloat(((acum / totalVendas50) * 100).toFixed(1))
        : 0;
    });

    /* ── Matriz de cruzamento: Classe × Cobertura ── */
    const COBS = ["ruptura", "normal", "excesso", "infinito"];
    const matriz = {};
    ["A","B","C","D"].forEach((cl) => {
      matriz[cl] = {};
      COBS.forEach((k) => { matriz[cl][k] = { count: 0, estoqueValor: 0, vendas: 0 }; });
    });
    listaCurva.forEach((item) => {
      const cl   = item.curva_classe;
      const dias = item.cobertura_dias || 0;
      if (!matriz[cl]) return;
      let cobKey;
      if (dias >= 9999)     cobKey = "infinito";
      else if (dias > 90)   cobKey = "excesso";
      else if (dias >= 30)  cobKey = "normal";
      else                  cobKey = "ruptura";
      matriz[cl][cobKey].count++;
      matriz[cl][cobKey].estoqueValor += item.estoque_valor    || 0;
      matriz[cl][cobKey].vendas       += item.curva_totalvenda || 0;
    });

    return { qtdClasse, vendasClasse, estoqueClasse, qtdSituacao, vendasLoja, lojaStacked, top10, pareto50, paretoAcum, matriz };
  }, [listaCurva]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─── Colunas da tabela principal ─── */
  const FS    = 11.5;
  const CELL  = { fontSize: FS, padding: "7px 8px", textAlign: "left" };
  const NR    = { ...CELL, textAlign: "right" };
  const CTR   = { ...CELL, textAlign: "center" };

  /* Caret customizado: sem seta quando não ordenado, seta simples quando ordenado */
  const sortCaret = (order) => {
    if (!order) return null;
    return (
      <span style={{ marginLeft: 4, color: "#e74c3c", fontSize: 10 }}>
        {order === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  const colunas = [
    {
      dataField: "curva_classificacao",
      text: "#",
      sort: true,
      sortCaret,
      headerStyle: { width: "46px", textAlign: "center", fontSize: 10.5 },
      style: CTR,
      formatter: (v, row) =>
        row.curva_classe === "D" ? (
          <span style={{ color: "#b2bec3" }}>—</span>
        ) : (
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 22, height: 22,
            background: v <= 3 ? "linear-gradient(135deg,#fef3c7,#fde68a)" : "#f1f5f9",
            borderRadius: 3, fontSize: 10, fontWeight: 700,
            color: v <= 3 ? "#92400e" : "#7f8c8d",
          }}>
            {v}
          </span>
        ),
    },
    {
      dataField: "fantasia",
      text: "Loja",
      sort: true,
      sortCaret,
      headerStyle: { width: "120px", fontSize: 10.5, textAlign: "left" },
      style: { ...CELL, textAlign: "left" },
      formatter: (v, row) => (
        <div style={{ textAlign: "left" }}>
          <div style={{ fontWeight: 600, color: "#2c3e50", fontSize: FS }}>{v}</div>
          {row.cnpj && (
            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 1, fontFamily: "monospace" }}>
              {fmtCnpj(row.cnpj)}
            </div>
          )}
        </div>
      ),
    },
    {
      dataField: "produto",
      text: "Produto",
      sort: true,
      sortCaret,
      align: "left",
      headerAlign: "left",
      headerStyle: { fontSize: 10.5 },
      style: { ...CELL, textAlign: "left" },
      formatter: (v, row) => (
        <div style={{ textAlign: "left" }}>
          <div style={{ fontWeight: 600, color: "#2c3e50", fontSize: FS, textAlign: "left" }}>{v}</div>
          {row.cod_barras && (
            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 1, textAlign: "left", fontFamily: "monospace", letterSpacing: "0.02em" }}>
              <i className="fa fa-barcode mr-1" style={{ fontSize: 9 }} />{row.cod_barras}
            </div>
          )}
          {row.fornecedor && (
            <div style={{ fontSize: 10.5, color: "#b2bec3", marginTop: 1, textAlign: "left" }}>{row.fornecedor}</div>
          )}
        </div>
      ),
    },
    {
      dataField: "categoria",
      text: "Cat.",
      sort: true,
      sortCaret,
      headerStyle: { width: "100px", textAlign: "center", fontSize: 10.5 },
      style: CTR,
      formatter: (v) => (
        <span style={{
          display: "inline-block", padding: "1px 6px",
          background: "#E0F2FE", color: "#3498db",
          borderRadius: 3, fontSize: 10.5, fontWeight: 700,
          whiteSpace: "nowrap",
        }}>
          {v}
        </span>
      ),
    },
    {
      dataField: "curva_qtvendida",
      text: "Qtde",
      sort: true,
      sortCaret,
      headerStyle: { width: "66px", textAlign: "right", fontSize: 10.5 },
      style: { ...NR, fontWeight: 600 },
      formatter: (v) => fmtInt(v),
    },
    {
      dataField: "curva_totalvenda",
      text: "Vendas",
      sort: true,
      sortCaret,
      headerStyle: { width: "100px", textAlign: "right", fontSize: 10.5 },
      style: { ...NR, fontWeight: 700, color: "#2c3e50" },
      formatter: (v) => v > 0
        ? <span>R$ {fmtNum(v)}</span>
        : <span style={{ color: "#b2bec3" }}>—</span>,
    },
    {
      dataField: "perc_acum",
      text: "Acum.",
      sort: true,
      sortCaret,
      headerStyle: { width: "80px", textAlign: "right", fontSize: 10.5 },
      style: NR,
      formatter: (v, row) =>
        v !== null && v !== undefined ? (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#2c3e50" }}>{v}%</div>
            <div style={{ height: 3, background: "#e8ecf0", borderRadius: 9999, marginTop: 2, overflow: "hidden" }}>
              <div style={{
                height: "100%", width: `${Math.min(v, 100)}%`,
                background: row.curva_classe === "A"
                  ? "linear-gradient(90deg,#27ae60,#34d399)"
                  : row.curva_classe === "B"
                  ? "linear-gradient(90deg,#D97706,#fbbf24)"
                  : "linear-gradient(90deg,#DC2626,#f87171)",
                borderRadius: 9999,
              }} />
            </div>
          </div>
        ) : <span style={{ color: "#b2bec3" }}>—</span>,
    },
    {
      dataField: "curva_classe",
      text: "Classe",
      sort: true,
      sortCaret,
      headerStyle: { width: "70px", textAlign: "center", fontSize: 10.5 },
      style: CTR,
      formatter: (v) => CLASSE_STYLE[v]
        ? <Badge text={v} style={CLASSE_STYLE[v]} />
        : v,
    },
    {
      dataField: "prod_saldo",
      text: "Saldo",
      sort: true,
      sortCaret,
      headerStyle: { width: "62px", textAlign: "right", fontSize: 10.5 },
      style: { ...NR, fontWeight: 600 },
      formatter: (v) => fmtInt(v),
    },
    {
      dataField: "estoque_valor",
      text: "Estoque",
      sort: true,
      sortCaret,
      headerStyle: { width: "100px", textAlign: "right", fontSize: 10.5 },
      style: NR,
      formatter: (v) => v > 0
        ? <span>R$ {fmtNum(v)}</span>
        : <span style={{ color: "#b2bec3" }}>—</span>,
    },
    {
      dataField: "cobertura_dias",
      text: "Cobert. / 30d",
      sort: true,
      sortCaret,
      headerStyle: { width: "90px", textAlign: "center", fontSize: 10.5 },
      style: CTR,
      formatter: (rawV, row) => {
        const v   = rawV < 0 ? 0 : rawV;          /* saldo negativo → cobertura 0 */
        const sit = SITUACAO_STYLE[row.situacao] || SITUACAO_STYLE.NORMAL;
        /* ∞ — estoque sem giro / saldo infinito */
        if (v >= 9999) {
          return (
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: sit.color, lineHeight: 1 }}>∞</div>
              <div style={{ height: 3, background: sit.color, borderRadius: 9999, marginTop: 4, opacity: 0.25 }} />
            </div>
          );
        }
        /* barra: 30d = 100 % (referência crítica de reabastecimento) */
        const perc = Math.min((v / 30) * 100, 100);
        return (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: sit.color, lineHeight: 1 }}>
              {fmtInt(v)} dias
            </div>
            <div style={{ height: 3, background: "#e8ecf0", borderRadius: 9999, marginTop: 4, overflow: "hidden" }}>
              <div style={{
                height: "100%",
                width: `${perc}%`,
                background: sit.color,
                borderRadius: 9999,
              }} />
            </div>
          </div>
        );
      },
    },
    {
      dataField: "situacao",
      text: "Situação",
      sort: true,
      sortCaret,
      headerStyle: { width: "90px", textAlign: "center", fontSize: 10.5 },
      style: CTR,
      formatter: (v) => SITUACAO_STYLE[v]
        ? <Badge text={SITUACAO_LABEL[v] || v} style={SITUACAO_STYLE[v]} />
        : v,
    },
  ];

  /* ─── Colunas do resumo por loja ─── */
  const colunasLoja = [
    {
      dataField: "fantasia",
      text: "Loja",
      sort: true,
      sortCaret,
      headerStyle: { textAlign: "left" },
      style: { textAlign: "left" },
      formatter: (v, row) => (
        <button
          style={{
            background: "none",
            border: "none",
            padding: 0,
            color: "#e74c3c",
            fontWeight: 700,
            cursor: "pointer",
            fontSize: 13,
            textAlign: "left",
            width: "100%",
            display: "block",
          }}
          onClick={() => {
            setFiltroLoja(String(row.filial));
            setActiveTab("1");
          }}
        >
          {v}
        </button>
      ),
    },
    {
      dataField: "A",
      text: "A",
      sort: true,
      sortCaret,
      headerStyle: { width: "60px", textAlign: "center" },
      style: { textAlign: "center" },
      formatter: (v) => (
        <span style={{ fontWeight: 700, color: "#065f46" }}>{v}</span>
      ),
    },
    {
      dataField: "B",
      text: "B",
      sort: true,
      sortCaret,
      headerStyle: { width: "60px", textAlign: "center" },
      style: { textAlign: "center" },
      formatter: (v) => (
        <span style={{ fontWeight: 700, color: "#92400e" }}>{v}</span>
      ),
    },
    {
      dataField: "C",
      text: "C",
      sort: true,
      sortCaret,
      headerStyle: { width: "60px", textAlign: "center" },
      style: { textAlign: "center" },
      formatter: (v) => (
        <span style={{ fontWeight: 700, color: "#991b1b" }}>{v}</span>
      ),
    },
    {
      dataField: "D",
      text: "D (Parado)",
      sort: true,
      sortCaret,
      headerStyle: { width: "80px", textAlign: "center" },
      style: { textAlign: "center" },
      formatter: (v) => (
        <span style={{ fontWeight: 700, color: "#374151" }}>{v}</span>
      ),
    },
    {
      dataField: "ruptura",
      text: "⚠ Ruptura",
      sort: true,
      sortCaret,
      headerStyle: { width: "90px", textAlign: "center" },
      style: { textAlign: "center" },
      formatter: (v) => (
        <span style={{ fontWeight: 700, color: v > 0 ? "#b91c1c" : "#b2bec3" }}>
          {v}
        </span>
      ),
    },
    {
      dataField: "excesso",
      text: "Excesso",
      sort: true,
      sortCaret,
      headerStyle: { width: "80px", textAlign: "center" },
      style: { textAlign: "center" },
      formatter: (v) => (
        <span style={{ fontWeight: 700, color: v > 0 ? "#713f12" : "#b2bec3" }}>
          {v}
        </span>
      ),
    },
    {
      dataField: "total_vendas",
      text: "Vendas",
      sort: true,
      sortCaret,
      headerStyle: { textAlign: "right" },
      style: { textAlign: "right", fontWeight: 700 },
      formatter: (v) => <span>R$ {fmtNum(v)}</span>,
    },
    {
      dataField: "capital_morto",
      text: "Capital Morto",
      sort: true,
      sortCaret,
      headerStyle: { textAlign: "right" },
      style: { textAlign: "right", color: "#991b1b", fontWeight: 700 },
      formatter: (v) => (v > 0 ? <span>R$ {fmtNum(v)}</span> : <span style={{ color: "#b2bec3" }}>—</span>),
    },
  ];

  /* ────────────────────────────────── */
  if (loading) {
    return (
      <div className="content">
        <Loading tipo="componente" />
      </div>
    );
  }

  /* ─── Helper: card de resumo ─── */
  const CardResumo = ({ titulo, valor, sub, sub2, accent, onClick, ativo }) => (
    <Col md="2" className="mb-3">
      <div
        onClick={onClick}
        style={{
          background: "#fff",
          borderRadius: 8,
          padding: "14px 16px",
          boxShadow: ativo ? "0 8px 24px rgba(0,0,0,.14)" : "0 2px 10px rgba(0,0,0,.08)",
          borderTop: `4px solid ${accent}`,
          cursor: onClick ? "pointer" : "default",
          transition: "transform 180ms, box-shadow 180ms",
          transform: ativo ? "translateY(-2px)" : "none",
          minHeight: 90,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div style={{
          fontSize: 10.5, fontWeight: 700, color: "#7f8c8d",
          textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4,
        }}>
          {titulo}
        </div>
        <div style={{
          fontSize: valor && valor.toString().length > 6 ? 18 : 24,
          fontWeight: 800, color: "#2c3e50", lineHeight: 1, letterSpacing: "-0.5px",
        }}>
          {valor}
        </div>
        <div style={{ marginTop: 4 }}>
          {sub && (
            <div style={{
              fontSize: 11, color: "#b2bec3",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {sub}
            </div>
          )}
          {sub2 && (
            <div style={{ fontSize: 10.5, color: "#b2bec3", marginTop: 1 }}>{sub2}</div>
          )}
        </div>
      </div>
    </Col>
  );

  return (
    <div className="content">
      <Container fluid>

        {/* ════ FILTROS ════ */}
        <Card className="mb-3">
          <CardBody style={{ padding: "16px 20px 8px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              {/* Título */}
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{
                  width: 4, height: 18, borderRadius: 2,
                  background: "linear-gradient(180deg,#e74c3c,#c0392b)",
                  display: "inline-block",
                }} />
                <span style={{ fontWeight: 700, fontSize: 14, color: "#2c3e50" }}>
                  Curva ABCD — Análise de Estoque
                </span>
              </div>

              {/* Engrenagem + popover de configuração ABC */}
              <div style={{ position: "relative" }} ref={configRef}>
                <button
                  type="button"
                  title="Configurar percentuais A/B/C"
                  onClick={() => {
                    setPercTemp({ ...percABC });
                    setShowConfigABC((v) => !v);
                  }}
                  style={{
                    background: percCustom ? "#FEF3C7" : "none",
                    border: percCustom ? "1px solid #FCD34D" : "1px solid #e0e0e0",
                    borderRadius: 6, padding: "3px 8px",
                    cursor: "pointer", color: percCustom ? "#92400e" : "#7f8c8d",
                    fontSize: 12, display: "flex", alignItems: "center", gap: 5,
                  }}
                >
                  <i className="fa fa-cog" style={{ fontSize: 13 }} />
                  <span>
                    {percCustom
                      ? `A:${percABC.A}% B:${percABC.B}% C:${percABC.C}%`
                      : "Parâmetros ABC"}
                  </span>
                </button>

                {showConfigABC && (
                  <div style={{
                    position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 1050,
                    background: "#fff", border: "1px solid #e2e8f0",
                    borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,.12)",
                    padding: "16px 18px", width: 280,
                  }}>
                    <div style={{ fontWeight: 700, fontSize: 12.5, color: "#2c3e50", marginBottom: 12 }}>
                      <i className="fa fa-sliders mr-1" /> Percentuais de corte A/B/C
                    </div>

                    {/* Inputs */}
                    <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                      {[
                        { key: "A", label: "Classe A", color: "#27ae60" },
                        { key: "B", label: "Classe B", color: "#D97706" },
                        { key: "C", label: "Classe C", color: "#DC2626" },
                      ].map(({ key, label, color }) => (
                        <div key={key} style={{ flex: 1 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color, marginBottom: 3 }}>{label}</div>
                          <div style={{ position: "relative" }}>
                            <input
                              type="number"
                              min={1} max={98}
                              value={percTemp[key]}
                              onChange={(e) => {
                                const v = Math.max(1, Math.min(98, Number(e.target.value) || 0));
                                setPercTemp((prev) => ({ ...prev, [key]: v }));
                              }}
                              style={{
                                width: "100%", textAlign: "center",
                                border: `1px solid ${color}40`,
                                borderRadius: 6, padding: "5px 18px 5px 6px",
                                fontSize: 14, fontWeight: 700, color,
                                outline: "none",
                              }}
                            />
                            <span style={{
                              position: "absolute", right: 5, top: "50%",
                              transform: "translateY(-50%)",
                              fontSize: 11, color, fontWeight: 600, pointerEvents: "none",
                            }}>%</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Indicador de soma */}
                    {(() => {
                      const soma = percTemp.A + percTemp.B + percTemp.C;
                      const ok   = soma === 100;
                      return (
                        <div style={{
                          fontSize: 11.5, fontWeight: 600, marginBottom: 12,
                          color: ok ? "#065f46" : "#b91c1c",
                          background: ok ? "#ECFDF5" : "#FEF2F2",
                          border: `1px solid ${ok ? "#A7F3D0" : "#FECACA"}`,
                          borderRadius: 6, padding: "4px 10px",
                          display: "flex", alignItems: "center", gap: 5,
                        }}>
                          <i className={`fa fa-${ok ? "check-circle" : "exclamation-circle"}`} />
                          {ok ? "Soma: 100% — OK" : `Soma: ${soma}% — ${soma < 100 ? `faltam ${100 - soma}%` : `excede ${soma - 100}%`}`}
                        </div>
                      );
                    })()}

                    {/* Botões */}
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        type="button"
                        style={{
                          flex: 1, padding: "6px 0", borderRadius: 6, fontSize: 12, fontWeight: 600,
                          background: "#e74c3c", color: "#fff", border: "none", cursor: "pointer",
                          opacity: percTemp.A + percTemp.B + percTemp.C !== 100 ? 0.45 : 1,
                        }}
                        disabled={percTemp.A + percTemp.B + percTemp.C !== 100}
                        onClick={() => {
                          const novo = { ...percTemp };
                          setPercABC(novo);
                          try { localStorage.setItem(PERC_ABC_KEY, JSON.stringify(novo)); } catch {}
                          setShowConfigABC(false);
                        }}
                      >
                        <i className="fa fa-check mr-1" /> Aplicar
                      </button>
                      <button
                        type="button"
                        style={{
                          padding: "6px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                          background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0", cursor: "pointer",
                        }}
                        onClick={() => {
                          setPercTemp({ ...PERC_ABC_DEFAULT });
                          setPercABC({ ...PERC_ABC_DEFAULT });
                          try { localStorage.removeItem(PERC_ABC_KEY); } catch {}
                          setShowConfigABC(false);
                        }}
                        title="Restaurar padrão (80/15/5)"
                      >
                        <i className="fa fa-undo" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Linha 1: Lojas | Produto */}
            <Row style={{ marginBottom: 0 }}>
              <Col md="6" className="mb-2">
                <Label style={{ fontSize: 11, fontWeight: 600, color: "#7f8c8d", marginBottom: 3, display: "flex", alignItems: "center", gap: 6 }}>
                  <span>Lojas <span style={{ color: "#e74c3c" }}>*</span></span>
                  <button
                    type="button"
                    style={{
                      background: "none", border: "none", padding: 0,
                      color: "#3498db", fontSize: 10.5, fontWeight: 600,
                      cursor: "pointer", textDecoration: "underline", lineHeight: 1,
                    }}
                    onClick={() => {
                      setSelectedLojas(lojas);
                      onChangeSelect("filialSelect")(lojas);
                    }}
                  >
                    Selecionar todas
                  </button>
                  {selectedLojas && selectedLojas.length > 0 && (
                    <button
                      type="button"
                      style={{
                        background: "none", border: "none", padding: 0,
                        color: "#b2bec3", fontSize: 10.5, fontWeight: 600,
                        cursor: "pointer", textDecoration: "underline", lineHeight: 1,
                      }}
                      onClick={() => {
                        setSelectedLojas(null);
                        onChangeSelect("filialSelect")([]);
                      }}
                    >
                      Limpar
                    </button>
                  )}
                </Label>
                <ReactSelect
                  isMulti
                  name="filialSelect"
                  placeholder="Selecione as lojas..."
                  options={lojas}
                  value={selectedLojas}
                  onChange={(vals) => {
                    setSelectedLojas(vals);
                    onChangeSelect("filialSelect")(vals);
                  }}
                />
              </Col>
              <Col md="6" className="mb-2">
                <Label style={{ fontSize: 11, fontWeight: 600, color: "#7f8c8d", marginBottom: 3 }}>
                  Produto
                </Label>
                <AsyncSelect
                  isMulti
                  loadOptions       = {buscaProdutos}
                  onChange          = {onChangeSelect("produtoSelect")}
                  formatOptionLabel = {(opt) => (
                    <div>
                      <div style={{ fontSize: '13px' }}>{opt.label}</div>
                      {opt.cod_barras && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' }}><i className="ti ti-barcode" style={{ fontSize: '12px', marginRight: '4px' }} aria-hidden="true" />{opt.cod_barras}</div>}
                    </div>
                  )}
                  placeholder="Buscar produto (mín. 3 letras)..."
                  noOptionsMessage={() => "Nenhum produto encontrado"}
                  loadingMessage={() => "Buscando..."}
                />
              </Col>
            </Row>

            {/* Linha 2: De | Até | Categoria | Fornecedor | Pesquisar */}
            <Row className="align-items-end" style={{ marginBottom: 4 }}>
              <Col md="2" className="mb-2">
                <Label style={{ fontSize: 11, fontWeight: 600, color: "#7f8c8d", marginBottom: 3 }}>
                  Data início
                </Label>
                <DateTime
                  timeFormat={false}
                  dateFormat="DD/MM/YYYY"
                  closeOnSelect
                  inputProps={{
                    placeholder: "dd/mm/aaaa",
                    className: "form-control",
                    style: { height: 38 },
                  }}
                  value={form.inicio || ""}
                  onChange={onChangeDate("inicio")}
                />
              </Col>
              <Col md="2" className="mb-2">
                <Label style={{ fontSize: 11, fontWeight: 600, color: "#7f8c8d", marginBottom: 3 }}>
                  Data fim
                </Label>
                <DateTime
                  timeFormat={false}
                  dateFormat="DD/MM/YYYY"
                  closeOnSelect
                  inputProps={{
                    placeholder: "dd/mm/aaaa",
                    className: "form-control",
                    style: { height: 38 },
                  }}
                  value={form.final || ""}
                  onChange={onChangeDate("final")}
                />
              </Col>
              <Col md="3" className="mb-2">
                <Label style={{ fontSize: 11, fontWeight: 600, color: "#7f8c8d", marginBottom: 3 }}>
                  Categoria
                </Label>
                <ReactSelect
                  isMulti
                  name="categoriaSelect"
                  placeholder="Todas..."
                  options={categorias}
                  onChange={onChangeSelect("categoriaSelect")}
                />
              </Col>
              <Col md="3" className="mb-2">
                <Label style={{ fontSize: 11, fontWeight: 600, color: "#7f8c8d", marginBottom: 3 }}>
                  Fornecedor
                </Label>
                <ReactSelect
                  isMulti
                  name="fornecedorSelect"
                  placeholder="Todos..."
                  options={fornecedores}
                  onChange={onChangeSelect("fornecedorSelect")}
                />
              </Col>
              <Col md="2" className="mb-0">
                <Button
                  color="danger"
                  block
                  onClick={pesquisar}
                  disabled={loadingTabela}
                  style={{ height: 38 }}
                >
                  <i className="fa fa-search" /> Pesquisar
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* ════ CARDS RESUMO ════ */}
        {resumo && (
          <Row className="mb-2">
            <CardResumo
              titulo="Classe A"
              valor={contagensABC ? contagensABC.A : resumo.A.itens}
              sub={percCustom ? `${percABC.A}% do giro` : `R$ ${resumo.A.valor}`}
              accent="#27ae60"
              onClick={() =>
                setFiltroClasse(filtroClasse === "A" ? "" : "A")
              }
              ativo={filtroClasse === "A"}
            />
            <CardResumo
              titulo="Classe B"
              valor={contagensABC ? contagensABC.B : resumo.B.itens}
              sub={percCustom ? `${percABC.B}% do giro` : `R$ ${resumo.B.valor}`}
              accent="#D97706"
              onClick={() =>
                setFiltroClasse(filtroClasse === "B" ? "" : "B")
              }
              ativo={filtroClasse === "B"}
            />
            <CardResumo
              titulo="Classe C"
              valor={contagensABC ? contagensABC.C : resumo.C.itens}
              sub={percCustom ? `${percABC.C}% do giro` : `R$ ${resumo.C.valor}`}
              accent="#DC2626"
              onClick={() =>
                setFiltroClasse(filtroClasse === "C" ? "" : "C")
              }
              ativo={filtroClasse === "C"}
            />
            <CardResumo
              titulo="D — Parado"
              valor={resumo.D.itens}
              sub={`Capital: R$ ${resumo.D.capital_morto}`}
              accent="#6b7280"
              onClick={() =>
                setFiltroClasse(filtroClasse === "D" ? "" : "D")
              }
              ativo={filtroClasse === "D"}
            />
            <CardResumo
              titulo="⚠ Ruptura"
              valor={resumo.ruptura.itens}
              sub="A/B com cob. < 30d"
              accent="#b91c1c"
              onClick={() =>
                setFiltroClasse(filtroClasse === "RUPTURA" ? "" : "RUPTURA")
              }
              ativo={filtroClasse === "RUPTURA"}
            />
            <CardResumo
              titulo="Excesso"
              valor={resumo.excesso.itens}
              sub="Cob. > 90 dias"
              accent="#713f12"
              onClick={() =>
                setFiltroClasse(filtroClasse === "EXCESSO" ? "" : "EXCESSO")
              }
              ativo={filtroClasse === "EXCESSO"}
            />
          </Row>
        )}

        {/* ════ ÁREA DE RESULTADO ════ */}
        {(listaCurva.length > 0 || loadingTabela) && (
          <Card>
            <CardHeader>
              {/* Linha 1: título + info | Excel PDF */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <CardTitle tag="h5" className="mb-0" style={{ margin: 0, lineHeight: 1 }}>Resultado</CardTitle>
                  <span style={{ fontSize: 12, color: "#7f8c8d", lineHeight: 1 }}>
                    {listaFiltrada.length} de {listaCurva.length} itens
                    {resumo && ` · Período: ${resumo.dias_periodo} dias · Total vendas: R$ ${resumo.total_vendas}`}
                  </span>
                </div>
                <Button
                  size="sm" color="primary"
                  onClick={() => setModalExportar(true)}
                  disabled={!listaFiltrada.length}
                  title="Exportar / Imprimir"
                >
                  <i className="fa fa-download" /> Exportar
                </Button>
              </div>

              {/* Linha 2: filtros centralizados */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>

                {/* Busca por texto */}
                <div style={{ position: "relative" }}>
                  <i className="fa fa-search" style={{
                    position: "absolute", left: 8, top: "50%",
                    transform: "translateY(-50%)", color: "#b2bec3",
                    fontSize: 12, pointerEvents: "none",
                  }} />
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Buscar produto, fornecedor, categoria, cód. de barras..."
                    value={filtroTexto}
                    onChange={(e) => setFiltroTexto(e.target.value)}
                    style={{ paddingLeft: 26, paddingRight: 24, width: 280 }}
                  />
                  {filtroTexto && (
                    <button
                      onClick={() => setFiltroTexto("")}
                      style={{
                        position: "absolute", right: 6, top: "50%",
                        transform: "translateY(-50%)", background: "none",
                        border: "none", padding: 0, cursor: "pointer",
                        color: "#b2bec3", fontSize: 15, lineHeight: 1,
                      }}
                    >×</button>
                  )}
                </div>

                {/* Filtro loja */}
                {lojasNaLista.length > 1 && (
                  <select
                    className="form-control form-control-sm"
                    style={{ width: 160 }}
                    value={filtroLoja}
                    onChange={(e) => setFiltroLoja(e.target.value)}
                  >
                    <option value="">Todas as lojas</option>
                    {lojasNaLista.map((l) => (
                      <option key={l.id} value={l.id}>{l.nome}</option>
                    ))}
                  </select>
                )}

                {/* Filtro classe */}
                <select
                  className="form-control form-control-sm"
                  style={{ width: 160 }}
                  value={filtroClasse}
                  onChange={(e) => {
                    setFiltroClasse(e.target.value);
                    setFiltroSituacao("");
                  }}
                >
                  <option value="">Todas as classes</option>
                  <option value="A">Classe A</option>
                  <option value="B">Classe B</option>
                  <option value="C">Classe C</option>
                  <option value="D">Classe D (Parado)</option>
                  <option value="RUPTURA">Ruptura</option>
                  <option value="EXCESSO">Excesso</option>
                </select>

                {/* Limpar filtros */}
                {(filtroClasse || filtroLoja || filtroSituacao || filtroTexto) && (
                  <Button
                    size="sm"
                    color="secondary"
                    onClick={() => {
                      setFiltroClasse("");
                      setFiltroLoja("");
                      setFiltroSituacao("");
                      setFiltroTexto("");
                    }}
                  >
                    <i className="fa fa-times" /> Limpar filtros
                  </Button>
                )}

              </div>
            </CardHeader>

            <CardBody className="p-0">
              {/* Tabs */}
              <Nav tabs style={{ paddingLeft: 20, paddingTop: 10 }}>
                <NavItem>
                  <NavLink
                    style={{ cursor: "pointer" }}
                    className={activeTab === "1" ? "active" : ""}
                    onClick={() => setActiveTab("1")}
                  >
                    <i className="fa fa-list" /> Consolidado
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    style={{ cursor: "pointer" }}
                    className={activeTab === "2" ? "active" : ""}
                    onClick={() => setActiveTab("2")}
                  >
                    <i className="fa fa-building" /> Por Loja
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    style={{ cursor: "pointer" }}
                    className={activeTab === "3" ? "active" : ""}
                    onClick={() => setActiveTab("3")}
                  >
                    <i className="fa fa-bar-chart" /> Gráficos
                  </NavLink>
                </NavItem>
                {lojasNaLista.length > 1 && (
                  <NavItem>
                    <NavLink
                      style={{ cursor: "pointer" }}
                      className={activeTab === "4" ? "active" : ""}
                      onClick={() => setActiveTab("4")}
                    >
                      <i className="fa fa-exchange" /> Transferências
                      {sugestoesTransferencia.filter((s) => s.prioridade === "ALTA").length > 0 && (
                        <span style={{
                          marginLeft: 6, background: "#b91c1c", color: "#fff",
                          borderRadius: 9999, fontSize: 10, fontWeight: 700,
                          padding: "1px 6px", verticalAlign: "middle",
                        }}>
                          {sugestoesTransferencia.filter((s) => s.prioridade === "ALTA").length}
                        </span>
                      )}
                    </NavLink>
                  </NavItem>
                )}
              </Nav>

              {/* Overlay de carregamento — cobre qualquer aba ativa */}
              <div style={{ position: "relative" }}>
                {loadingTabela && (
                  <div style={{
                    position: "absolute", inset: 0,
                    background: "rgba(255,255,255,0.82)",
                    zIndex: 20,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    borderRadius: "0 0 4px 4px",
                    minHeight: 120,
                  }}>
                    <Loading tipo="componente" />
                  </div>
                )}

              <TabContent activeTab={activeTab}>
                {/* TAB 1 — CONSOLIDADO */}
                <TabPane tabId="1">
                  {listaFiltrada.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "60px 0",
                        color: "#7f8c8d",
                        fontSize: 14,
                      }}
                    >
                      <i
                        className="fa fa-inbox"
                        style={{ fontSize: 32, display: "block", marginBottom: 10 }}
                      />
                      Nenhum produto encontrado com os filtros selecionados.
                    </div>
                  ) : (
                    <BootstrapTable
                      keyField="produto_id_loja"
                      data={listaFiltrada.map((i, idx) => ({
                        ...i,
                        produto_id_loja: `${i.produto_id}_${i.filial}_${idx}`,
                      }))}
                      columns={colunas}
                      pagination={paginationFactory({
                        sizePerPage: 50,
                        sizePerPageList: [
                          { text: '25',  value: 25  },
                          { text: '50',  value: 50  },
                          { text: '100', value: 100 },
                          { text: '200', value: 200 },
                        ],
                        showTotal: true,
                        paginationTotalRenderer: (from, to, size) => (
                          <span className="react-bootstrap-table-pagination-total ml-2">
                            {from}–{to} de {size} itens
                          </span>
                        ),
                        sizePerPageRenderer: ({ options, currSizePerPage, onSizePerPageChange }) => (
                          <select
                            value={currSizePerPage}
                            onChange={e => onSizePerPageChange(Number(e.target.value))}
                            style={{
                              height: 32, padding: "0 6px",
                              border: "1px solid #dee2e6", borderRadius: 4,
                              fontSize: 13, cursor: "pointer",
                              background: "#fff", color: "#2c3e50",
                            }}
                          >
                            {options.map((opt, idx) => (
                              <option key={`spp-${opt.value ?? idx}`} value={opt.value}>{opt.text}</option>
                            ))}
                          </select>
                        ),
                      })}
                      bordered={false}
                      striped
                      hover
                      wrapperClasses="table-responsive"
                      headerClasses="thead-light"
                    />
                  )}
                </TabPane>

                {/* TAB 2 — POR LOJA */}
                <TabPane tabId="2">
                  {resumoPorLoja.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "60px 0",
                        color: "#7f8c8d",
                      }}
                    >
                      Sem dados.
                    </div>
                  ) : (
                    <>
                      {/* Alerta de ruptura por loja */}
                      {resumoPorLoja.some((l) => l.ruptura > 0) && (
                        <div
                          style={{
                            margin: "12px 20px",
                            padding: "12px 16px",
                            background: "#FEF2F2",
                            border: "1px solid #FECACA",
                            borderRadius: 8,
                            fontSize: 13,
                            color: "#991b1b",
                          }}
                        >
                          <strong>⚠ Alerta de Ruptura:</strong>{" "}
                          {resumoPorLoja
                            .filter((l) => l.ruptura > 0)
                            .map(
                              (l) =>
                                `${l.fantasia} (${l.ruptura} produto${l.ruptura > 1 ? "s" : ""})`
                            )
                            .join(" · ")}
                        </div>
                      )}

                      <BootstrapTable
                        keyField="filial"
                        data={resumoPorLoja}
                        columns={colunasLoja}
                        bordered={false}
                        striped
                        hover
                        wrapperClasses="table-responsive"
                        headerClasses="thead-light"
                      />

                      <div
                        style={{
                          padding: "0 20px 16px",
                          fontSize: 11.5,
                          color: "#7f8c8d",
                        }}
                      >
                        Clique no nome da loja para filtrar a tabela Consolidado.
                      </div>
                    </>
                  )}
                </TabPane>

                {/* TAB 3 — GRÁFICOS */}
                <TabPane tabId="3">
                  {!dadosGraficos ? (
                    <div style={{ textAlign: "center", padding: "60px 0", color: "#7f8c8d" }}>
                      Sem dados para exibir gráficos.
                    </div>
                  ) : (() => {
                    const { qtdClasse, vendasClasse, estoqueClasse, qtdSituacao,
                            vendasLoja, lojaStacked, top10, pareto50, paretoAcum, matriz } = dadosGraficos;

                    /* ── Opções reutilizáveis ── */
                    const optDonut = (fmtVal) => ({
                      responsive: true,
                      maintainAspectRatio: false,
                      cutoutPercentage: 62,
                      legend: {
                        display: true,
                        position: "right",
                        labels: { boxWidth: 11, padding: 8, fontSize: 11 },
                      },
                      tooltips: {
                        callbacks: {
                          label: function(item, data) {
                            const val = data.datasets[0].data[item.index];
                            const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
                            const pct = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
                            return ` ${data.labels[item.index]}: ${fmtVal(val)} (${pct}%)`;
                          },
                        },
                      },
                    });

                    const optHBar = (fmtTick) => ({
                      responsive: true,
                      maintainAspectRatio: false,
                      legend: { display: false },
                      tooltips: {
                        callbacks: {
                          label: (item) => ` ${fmtTick(item.xLabel)}`,
                        },
                      },
                      scales: {
                        xAxes: [{ ticks: { callback: fmtTick, fontSize: 10 }, gridLines: { color: "#f0f0f0" } }],
                        yAxes: [{ ticks: { fontSize: 10 } }],
                      },
                    });

                    const optBar = (fmtTick) => ({
                      responsive: true,
                      maintainAspectRatio: false,
                      legend: { display: false },
                      tooltips: {
                        callbacks: { label: (item) => ` ${fmtTick(item.yLabel)}` },
                      },
                      scales: {
                        yAxes: [{ ticks: { callback: fmtTick, fontSize: 10 }, gridLines: { color: "#f0f0f0" } }],
                        xAxes: [{ ticks: { fontSize: 10 } }],
                      },
                    });

                    const fmtR = (v) => `R$ ${fmtNum(v)}`;
                    const CORES_CLASSE = ["#27ae60","#D97706","#DC2626","#9ca3af"];

                    /* ── 1. Donut: Qtde por Classe ── */
                    const dataQtdClasse = {
                      labels: ["A","B","C","D"],
                      datasets: [{
                        data: [qtdClasse.A, qtdClasse.B, qtdClasse.C, qtdClasse.D],
                        backgroundColor: CORES_CLASSE,
                        borderWidth: 0,
                      }],
                    };

                    /* ── 2. Donut: Vendas por Classe ── */
                    const dataVendasClasse = {
                      labels: ["A","B","C","D"],
                      datasets: [{
                        data: [vendasClasse.A, vendasClasse.B, vendasClasse.C, vendasClasse.D],
                        backgroundColor: CORES_CLASSE,
                        borderWidth: 0,
                      }],
                    };

                    /* ── 3. Donut: Situação ── */
                    const ordemSit = ["NORMAL","RUPTURA","EXCESSO","ATENCAO","MORTO"];
                    const sitFiltradas = ordemSit.filter((s) => qtdSituacao[s] > 0);
                    const dataSituacao = {
                      labels: sitFiltradas,
                      datasets: [{
                        data: sitFiltradas.map((s) => qtdSituacao[s]),
                        backgroundColor: sitFiltradas.map((s) => COR_SITUACAO[s]),
                        borderWidth: 0,
                      }],
                    };

                    /* ── 4. Horizontal Bar: Top 10 Produtos ── */
                    const dataTop10 = {
                      labels: top10.map((p) =>
                        p.produto.length > 28 ? p.produto.substring(0, 28) + "…" : p.produto
                      ),
                      datasets: [{
                        data: top10.map((p) => p.curva_totalvenda || 0),
                        backgroundColor: top10.map((p) => COR_CLASSE[p.curva_classe] || "#9ca3af"),
                        borderWidth: 0,
                      }],
                    };

                    /* ── 5. Bar: Capital em Estoque por Classe ── */
                    const dataEstoque = {
                      labels: ["A","B","C","D"],
                      datasets: [{
                        label: "Estoque (R$)",
                        data: [estoqueClasse.A, estoqueClasse.B, estoqueClasse.C, estoqueClasse.D],
                        backgroundColor: CORES_CLASSE,
                        borderWidth: 0,
                      }],
                    };

                    /* ── 6. Bar Empilhado: Composição por Loja ── */
                    const lojaNames = Object.keys(lojaStacked);
                    const dataComposicao = {
                      labels: lojaNames,
                      datasets: [
                        { label: "A", data: lojaNames.map((l) => lojaStacked[l].A), backgroundColor: "#27ae60", stack: "comp", borderWidth: 0 },
                        { label: "B", data: lojaNames.map((l) => lojaStacked[l].B), backgroundColor: "#D97706", stack: "comp", borderWidth: 0 },
                        { label: "C", data: lojaNames.map((l) => lojaStacked[l].C), backgroundColor: "#DC2626", stack: "comp", borderWidth: 0 },
                        { label: "D", data: lojaNames.map((l) => lojaStacked[l].D), backgroundColor: "#9ca3af", stack: "comp", borderWidth: 0 },
                      ],
                    };
                    const optComposicao = {
                      responsive: true,
                      maintainAspectRatio: false,
                      legend: { display: true, position: "top", labels: { boxWidth: 11, fontSize: 11 } },
                      tooltips: { mode: "index" },
                      scales: {
                        xAxes: [{ stacked: true, ticks: { fontSize: 10 } }],
                        yAxes: [{ stacked: true, ticks: { fontSize: 10 } }],
                      },
                    };

                    /* ── 7. Pareto (Bar + Line): Curva ABC ── */
                    const dataPareto = {
                      labels: pareto50.map((_, i) => `#${i + 1}`),
                      datasets: [
                        {
                          type: "bar",
                          label: "Vendas (R$)",
                          data: pareto50.map((p) => p.curva_totalvenda || 0),
                          backgroundColor: pareto50.map((p) => COR_CLASSE[p.curva_classe] || "#9ca3af"),
                          borderWidth: 0,
                          yAxisID: "y-bars",
                        },
                        {
                          type: "line",
                          label: "% Acumulado",
                          data: paretoAcum,
                          borderColor: "#e74c3c",
                          backgroundColor: "rgba(231,76,60,0.06)",
                          pointRadius: 0,
                          borderWidth: 2,
                          fill: true,
                          yAxisID: "y-line",
                        },
                      ],
                    };
                    const optPareto = {
                      responsive: true,
                      maintainAspectRatio: false,
                      legend: { display: true, position: "top", labels: { boxWidth: 11, fontSize: 11 } },
                      tooltips: {
                        mode: "index",
                        callbacks: {
                          title: (items) => {
                            const idx = items[0].index;
                            const p = pareto50[idx];
                            return p ? p.produto.substring(0, 40) : "";
                          },
                          label: (item, data) => {
                            const ds = data.datasets[item.datasetIndex];
                            if (item.datasetIndex === 0)
                              return ` ${ds.label}: R$ ${fmtNum(item.yLabel)}`;
                            return ` ${ds.label}: ${item.yLabel}%`;
                          },
                        },
                      },
                      scales: {
                        xAxes: [{ ticks: { fontSize: 9 }, gridLines: { color: "#f0f0f0" } }],
                        yAxes: [
                          {
                            id: "y-bars",
                            position: "left",
                            ticks: { callback: (v) => `R$ ${fmtNum(v)}`, fontSize: 9 },
                            gridLines: { color: "#f0f0f0" },
                          },
                          {
                            id: "y-line",
                            position: "right",
                            ticks: { min: 0, max: 100, callback: (v) => `${v}%`, fontSize: 9 },
                            gridLines: { drawOnChartArea: false },
                          },
                        ],
                      },
                    };

                    /* ── Helpers visuais ── */
                    const GCard = ({ title, icon, children, height }) => (
                      <Card className="mb-3 h-100" style={{ border: "1px solid #f0f0f0" }}>
                        <CardHeader style={{ padding: "10px 16px", background: "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#2c3e50" }}>
                            <i className={`fa fa-${icon} mr-2`} style={{ color: "#e74c3c" }} />
                            {title}
                          </span>
                        </CardHeader>
                        <CardBody style={{ padding: "14px 16px" }}>
                          <div style={{ height: height || 220, position: "relative" }}>
                            {children}
                          </div>
                        </CardBody>
                      </Card>
                    );

                    /* ── Config visual das células da matriz ── */
                    const MATRIZ_CFG = {
                      A: {
                        ruptura:  { bg:"#FEF2F2", borda:"#FECACA", cor:"#b91c1c", acao:"Repor urgente",       icone:"⚡" },
                        normal:   { bg:"#F0FDF4", borda:"#A7F3D0", cor:"#065f46", acao:"Situação ideal",      icone:"✅" },
                        excesso:  { bg:"#FEFCE8", borda:"#FDE68A", cor:"#713f12", acao:"Capital excessivo",   icone:"⚠" },
                        infinito: { bg:"#F9FAFB", borda:"#E5E7EB", cor:"#9ca3af", acao:"—",                  icone:""  },
                      },
                      B: {
                        ruptura:  { bg:"#FFF7ED", borda:"#FED7AA", cor:"#9a3412", acao:"Repor em breve",      icone:"🟠" },
                        normal:   { bg:"#F0FDF4", borda:"#A7F3D0", cor:"#065f46", acao:"Situação ideal",      icone:"✅" },
                        excesso:  { bg:"#FEFCE8", borda:"#FDE68A", cor:"#713f12", acao:"Revisar pedidos",     icone:"⚠" },
                        infinito: { bg:"#F9FAFB", borda:"#E5E7EB", cor:"#9ca3af", acao:"—",                  icone:""  },
                      },
                      C: {
                        ruptura:  { bg:"#FEFCE8", borda:"#FDE68A", cor:"#713f12", acao:"Avaliar reposição",   icone:"🟡" },
                        normal:   { bg:"#F0FDF4", borda:"#A7F3D0", cor:"#065f46", acao:"OK",                  icone:"✅" },
                        excesso:  { bg:"#FFF7ED", borda:"#FED7AA", cor:"#9a3412", acao:"Excesso de capital",  icone:"🟠" },
                        infinito: { bg:"#F9FAFB", borda:"#E5E7EB", cor:"#9ca3af", acao:"—",                  icone:""  },
                      },
                      D: {
                        ruptura:  { bg:"#F9FAFB", borda:"#E5E7EB", cor:"#9ca3af", acao:"—",                  icone:""  },
                        normal:   { bg:"#F9FAFB", borda:"#E5E7EB", cor:"#9ca3af", acao:"—",                  icone:""  },
                        excesso:  { bg:"#FEF2F2", borda:"#FECACA", cor:"#991b1b", acao:"Capital morto",       icone:"🔴" },
                        infinito: { bg:"#FEF2F2", borda:"#FECACA", cor:"#991b1b", acao:"Capital morto",       icone:"🔴" },
                      },
                    };

                    const COB_COLS = [
                      { key:"ruptura",  label:"< 30 dias",  sub:"Ruptura / Crítico" },
                      { key:"normal",   label:"30 – 90 dias", sub:"Cobertura Normal" },
                      { key:"excesso",  label:"> 90 dias",  sub:"Excesso de estoque" },
                      { key:"infinito", label:"∞",          sub:"Sem giro registrado" },
                    ];

                    return (
                      <div style={{ padding: "16px 20px" }}>

                        {/* ═══ Matriz de Cruzamento ═══ */}
                        <Card className="mb-3" style={{ border: "1px solid #f0f0f0" }}>
                          <CardHeader style={{ padding: "12px 18px", background: "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
                            <div style={{ display:"flex", alignItems:"center", gap: 8 }}>
                              <span style={{ width:4, height:16, borderRadius:2, background:"linear-gradient(180deg,#e74c3c,#c0392b)", display:"inline-block" }} />
                              <span style={{ fontSize:13, fontWeight:700, color:"#2c3e50" }}>
                                Matriz de Cruzamento — Classe de Venda × Cobertura de Estoque
                              </span>
                            </div>
                            <div style={{ fontSize:11, color:"#7f8c8d", marginTop:3, marginLeft:12 }}>
                              Cada célula mostra a quantidade de produtos e o capital em estoque na interseção da classe com a faixa de cobertura
                            </div>
                          </CardHeader>
                          <CardBody style={{ padding: "16px 18px", overflowX:"auto" }}>
                            <table style={{ width:"100%", borderCollapse:"separate", borderSpacing: 5 }}>
                              <thead>
                                <tr>
                                  <th style={{ width:80, padding:"8px 12px", background:"#f8f9fa", borderRadius:6, fontSize:11, color:"#7f8c8d", textAlign:"center", verticalAlign:"middle" }}>
                                    Classe
                                  </th>
                                  {COB_COLS.map((c) => (
                                    <th key={c.key} style={{ padding:"8px 12px", background:"#f8f9fa", borderRadius:6, fontSize:11, textAlign:"center", verticalAlign:"middle" }}>
                                      <div style={{ fontWeight:700, color:"#2c3e50", fontSize:13 }}>{c.label}</div>
                                      <div style={{ color:"#9ca3af", fontWeight:400, marginTop:1 }}>{c.sub}</div>
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {["A","B","C","D"].map((cl) => (
                                  <tr key={cl}>
                                    {/* Label da classe */}
                                    <td style={{ padding:"8px 6px", textAlign:"center" }}>
                                      <span style={{
                                        display:"inline-flex", alignItems:"center", justifyContent:"center",
                                        width:36, height:36, borderRadius:8,
                                        background: COR_CLASSE[cl] + "22",
                                        border:`2px solid ${COR_CLASSE[cl]}`,
                                        fontSize:16, fontWeight:800, color: COR_CLASSE[cl],
                                      }}>
                                        {cl}
                                      </span>
                                    </td>
                                    {COB_COLS.map((c) => {
                                      const cell = matriz[cl][c.key];
                                      const cfg  = MATRIZ_CFG[cl][c.key];
                                      const vazio = cell.count === 0;
                                      return (
                                        <td key={c.key} style={{
                                          padding: 6,
                                          verticalAlign:"top",
                                        }}>
                                          <div
                                            onClick={() => {
                                              if (vazio) return;
                                              /* Clique filtra a tabela Consolidado */
                                              const cobMap = {
                                                ruptura:"RUPTURA", excesso:"EXCESSO",
                                                infinito:"MORTO",  normal:"NORMAL",
                                              };
                                              setFiltroClasse(cl);
                                              if (cobMap[c.key]) setFiltroSituacao(cobMap[c.key]);
                                              setActiveTab("1");
                                            }}
                                            style={{
                                              background: vazio ? "#f9fafb" : cfg.bg,
                                              border:`1px solid ${vazio ? "#e5e7eb" : cfg.borda}`,
                                              borderRadius:8,
                                              padding:"10px 12px",
                                              minHeight: 80,
                                              cursor: vazio ? "default" : "pointer",
                                              transition:"box-shadow 150ms",
                                              opacity: vazio ? 0.45 : 1,
                                            }}
                                          >
                                            {vazio ? (
                                              <div style={{ textAlign:"center", color:"#d1d5db", fontSize:18, paddingTop:12 }}>—</div>
                                            ) : (
                                              <>
                                                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                                                  <div style={{ fontSize:22, fontWeight:800, color: cfg.cor, lineHeight:1 }}>
                                                    {fmtInt(cell.count)}
                                                  </div>
                                                  <span style={{ fontSize:14 }}>{cfg.icone}</span>
                                                </div>
                                                <div style={{ fontSize:10, color:"#6b7280", marginTop:2 }}>
                                                  {cell.count === 1 ? "produto" : "produtos"}
                                                </div>
                                                {cell.estoqueValor > 0 && (
                                                  <div style={{ fontSize:10.5, fontWeight:600, color: cfg.cor, marginTop:4 }}>
                                                    R$ {fmtNum(cell.estoqueValor)}
                                                  </div>
                                                )}
                                                <div style={{
                                                  fontSize:10, color: cfg.cor, marginTop:4,
                                                  fontWeight:600, borderTop:`1px dashed ${cfg.borda}`,
                                                  paddingTop:4,
                                                }}>
                                                  {cfg.acao}
                                                </div>
                                              </>
                                            )}
                                          </div>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div style={{ fontSize:10.5, color:"#9ca3af", marginTop:8, textAlign:"right" }}>
                              Clique em uma célula para filtrar os produtos correspondentes na aba Consolidado
                            </div>
                          </CardBody>
                        </Card>

                        {/* Linha 1 — 3 Donuts */}
                        <Row>
                          <Col md="4">
                            <GCard title="Produtos por Classe" icon="pie-chart">
                              <Doughnut data={dataQtdClasse} options={optDonut((v) => fmtInt(v))} />
                            </GCard>
                          </Col>
                          <Col md="4">
                            <GCard title="Receita por Classe" icon="dollar">
                              <Doughnut data={dataVendasClasse} options={optDonut(fmtR)} />
                            </GCard>
                          </Col>
                          <Col md="4">
                            <GCard title="Situação do Estoque" icon="exclamation-triangle">
                              <Doughnut data={dataSituacao} options={optDonut((v) => fmtInt(v))} />
                            </GCard>
                          </Col>
                        </Row>

                        {/* Linha 2 — Top 10 + Capital em estoque */}
                        <Row className="mt-3">
                          <Col md="7">
                            <GCard title="Top 10 Produtos por Venda" icon="trophy" height={260}>
                              <HorizontalBar data={dataTop10} options={optHBar(fmtR)} />
                            </GCard>
                          </Col>
                          <Col md="5">
                            <GCard title="Capital em Estoque por Classe" icon="archive" height={260}>
                              <Bar data={dataEstoque} options={optBar(fmtR)} />
                            </GCard>
                          </Col>
                        </Row>

                        {/* Linha 3 — Composição por loja (só se > 1 loja) */}
                        {lojaNames.length > 1 && (
                          <Row>
                            <Col md="12">
                              <GCard title="Composição A/B/C/D por Loja" icon="building" height={200}>
                                <Bar data={dataComposicao} options={optComposicao} />
                              </GCard>
                            </Col>
                          </Row>
                        )}

                        {/* Linha 4 — Curva Pareto ABC */}
                        <Row className="mt-3">
                          <Col md="12">
                            <GCard title={`Curva Pareto ABC — Top ${pareto50.length} produtos (barras coloridas por classe · linha = % acumulado de vendas)`} icon="line-chart" height={280}>
                              <Bar data={dataPareto} options={optPareto} />
                            </GCard>
                          </Col>
                        </Row>

                        {/* Legenda das cores */}
                        <div style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 4, fontSize: 11, color: "#7f8c8d" }}>
                          {[["A","#27ae60"],["B","#D97706"],["C","#DC2626"],["D","#9ca3af"]].map(([cl, cor]) => (
                            <span key={cl} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              <span style={{ width: 12, height: 12, borderRadius: 2, background: cor, display: "inline-block" }} />
                              Classe {cl}
                            </span>
                          ))}
                        </div>

                      </div>
                    );
                  })()}
                </TabPane>
                {/* TAB 4 — TRANSFERÊNCIAS */}
                <TabPane tabId="4">
                  {(() => {
                    const PRIOR_CFG = {
                      ALTA:  { bg:"#FEF2F2", borda:"#FECACA", cor:"#b91c1c", label:"ALTA",  icone:"⚡" },
                      MEDIA: { bg:"#FFF7ED", borda:"#FED7AA", cor:"#9a3412", label:"MÉDIA", icone:"🟠" },
                      BAIXA: { bg:"#FEFCE8", borda:"#FDE68A", cor:"#713f12", label:"BAIXA", icone:"🟡" },
                    };

                    if (lojasNaLista.length < 2) {
                      return (
                        <div style={{ textAlign:"center", padding:"60px 20px", color:"#7f8c8d" }}>
                          <i className="fa fa-exchange" style={{ fontSize:32, display:"block", marginBottom:10, color:"#dfe6e9" }} />
                          Selecione mais de uma loja para ver as sugestões de transferência.
                        </div>
                      );
                    }

                    if (!sugestoesTransferencia.length) {
                      return (
                        <div style={{ textAlign:"center", padding:"60px 20px", color:"#7f8c8d" }}>
                          <i className="fa fa-check-circle" style={{ fontSize:32, display:"block", marginBottom:10, color:"#27ae60" }} />
                          <strong style={{ color:"#2c3e50" }}>Nenhuma transferência necessária</strong>
                          <p style={{ margin:"6px 0 0", fontSize:13 }}>
                            Não foram encontrados produtos com ruptura em uma loja e excesso em outra.
                          </p>
                        </div>
                      );
                    }

                    const totalAlta  = sugestoesTransferencia.filter((s) => s.prioridade === "ALTA").length;
                    const totalMedia = sugestoesTransferencia.filter((s) => s.prioridade === "MEDIA").length;
                    const totalBaixa = sugestoesTransferencia.filter((s) => s.prioridade === "BAIXA").length;
                    const valorTotal = sugestoesTransferencia.reduce((s, i) => s + (i.valor_estimado || 0), 0);

                    return (
                      <div style={{ padding:"16px 20px" }}>

                        {/* Cards resumo das sugestões */}
                        <div className="mb-3" style={{ display:"flex", gap:10 }}>
                          {[
                            { label:"TOTAL DE SUGESTÕES", valor: sugestoesTransferencia.length, cor:"#2c3e50", sub:"produtos a redistribuir", icone:"fa-exchange" },
                            { label:"ALTA PRIORIDADE",    valor: totalAlta,  cor:"#b91c1c", sub:"Classe A em ruptura",  icone:"fa-exclamation-circle" },
                            { label:"MÉDIA PRIORIDADE",   valor: totalMedia, cor:"#9a3412", sub:"Classe B em atenção",  icone:"fa-exclamation-triangle" },
                            { label:"BAIXA PRIORIDADE",   valor: totalBaixa, cor:"#713f12", sub:"Classe C",             icone:"fa-info-circle" },
                            { label:"VALOR ESTIMADO",     valor:`R$ ${fmtNum(valorTotal)}`, cor:"#065f46", sub:"nas transferências", icone:"fa-dollar" },
                          ].map((c, i) => (
                            <div key={i} style={{ flex:"1 1 0", minWidth:0 }}>
                              <div style={{
                                background:"#fff", borderRadius:8, padding:"12px 14px",
                                boxShadow:"0 2px 8px rgba(0,0,0,.07)",
                                borderLeft:`4px solid ${c.cor}`,
                                height:100,
                                display:"flex", flexDirection:"column", justifyContent:"space-between",
                              }}>
                                <div style={{ fontSize:10, fontWeight:700, color:"#7f8c8d", textTransform:"uppercase", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                                  <i className={`fa ${c.icone} mr-1`} />{c.label}
                                </div>
                                <div style={{ fontSize: typeof c.valor === "string" ? 15 : 26, fontWeight:800, color:c.cor, lineHeight:1 }}>{c.valor}</div>
                                <div style={{ fontSize:10.5, color:"#b2bec3" }}>{c.sub}</div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Aviso */}
                        <div style={{
                          background:"#f0f9ff", border:"1px solid #BAE6FD", borderRadius:8,
                          padding:"10px 16px", fontSize:12, color:"#0369a1", marginBottom:16,
                          display:"flex", alignItems:"flex-start", gap:10,
                        }}>
                          <i className="fa fa-info-circle" style={{ marginTop:1, flexShrink:0 }} />
                          <span>
                            <strong>Sugestões automáticas — valide antes de executar.</strong> O cálculo usa as vendas do período selecionado como referência.
                            Considera que a loja doadora mantenha pelo menos 30 dias de cobertura após a transferência.
                            Produtos Classe D são doadores prioritários (sem necessidade de estoque de segurança).
                          </span>
                        </div>

                        {/* Tabela de sugestões */}
                        {["ALTA","MEDIA","BAIXA"].map((prio) => {
                          const grupo = sugestoesTransferencia.filter((s) => s.prioridade === prio);
                          if (!grupo.length) return null;
                          const cfg = PRIOR_CFG[prio];
                          return (
                            <div key={prio} className="mb-4">
                              {/* Cabeçalho do grupo */}
                              <div style={{
                                background: cfg.bg, border:`1px solid ${cfg.borda}`,
                                borderRadius:"8px 8px 0 0", padding:"8px 16px",
                                display:"flex", alignItems:"center", gap:8,
                              }}>
                                <span style={{ fontSize:14 }}>{cfg.icone}</span>
                                <span style={{ fontWeight:700, color:cfg.cor, fontSize:13 }}>
                                  Prioridade {cfg.label} — {grupo.length} {grupo.length > 1 ? "sugestões" : "sugestão"}
                                </span>
                                <span style={{ fontSize:11, color:cfg.cor, marginLeft:4 }}>
                                  {prio === "ALTA" ? "Produtos Classe A em ruptura" : prio === "MEDIA" ? "Produtos Classe B em atenção" : "Produtos Classe C"}
                                </span>
                              </div>

                              {/* Linhas */}
                              <div style={{ border:`1px solid ${cfg.borda}`, borderTop:"none", borderRadius:"0 0 8px 8px", overflow:"hidden" }}>
                                {/* Header da tabela */}
                                <div style={{
                                  display:"grid",
                                  gridTemplateColumns:"3fr 60px 2fr 2fr 90px 110px",
                                  background:"#fafafa", borderBottom:`1px solid ${cfg.borda}`,
                                  padding:"6px 12px", fontSize:10.5, fontWeight:700, color:"#7f8c8d",
                                }}>
                                  <span>Produto</span>
                                  <span style={{ textAlign:"center" }}>Classe</span>
                                  <span>🏪 De (doadora)</span>
                                  <span>📦 Para (deficitária)</span>
                                  <span style={{ textAlign:"right" }}>Qtd. Sugerida</span>
                                  <span style={{ textAlign:"right" }}>Valor Est.</span>
                                </div>

                                {grupo.map((s, idx) => (
                                  <div key={s._key} style={{
                                    display:"grid",
                                    gridTemplateColumns:"3fr 60px 2fr 2fr 90px 110px",
                                    padding:"10px 12px",
                                    background: idx % 2 === 0 ? "#fff" : "#fafafa",
                                    borderBottom: idx < grupo.length - 1 ? `1px solid #f0f0f0` : "none",
                                    alignItems:"center",
                                    fontSize:12,
                                  }}>
                                    {/* Produto */}
                                    <div>
                                      <div style={{ fontWeight:600, color:"#2c3e50", fontSize:12 }}>{s.produto}</div>
                                      {s.cod_barras && (
                                        <div style={{ fontSize:10, color:"#94a3b8", marginTop:1, fontFamily:"monospace", letterSpacing:"0.02em" }}>
                                          <i className="fa fa-barcode mr-1" style={{ fontSize:9 }} />{s.cod_barras}
                                        </div>
                                      )}
                                      {s.categoria && (
                                        <span style={{
                                          display:"inline-block", marginTop:2,
                                          background:"#E0F2FE", color:"#3498db",
                                          borderRadius:3, fontSize:10, fontWeight:700, padding:"1px 5px",
                                        }}>
                                          {s.categoria}
                                        </span>
                                      )}
                                    </div>

                                    {/* Classe */}
                                    <div style={{ textAlign:"center" }}>
                                      <Badge text={s.classe} style={CLASSE_STYLE[s.classe] || CLASSE_STYLE.D} />
                                    </div>

                                    {/* Doadora */}
                                    <div>
                                      <div style={{ fontWeight:600, color:"#2c3e50", fontSize:12 }}>{s.doadora.fantasia}</div>
                                      <div style={{ fontSize:10.5, color:"#7f8c8d", marginTop:2 }}>
                                        Saldo: <strong>{fmtInt(s.doadora.saldo)}</strong>
                                        {" · "}
                                        {s.doadora.cobertura_dias >= 9999 ? "∞" : `${fmtInt(Math.max(0, s.doadora.cobertura_dias))} dias`} cobertura
                                      </div>
                                      <div style={{ marginTop:3 }}>
                                        <Badge
                                          text={SITUACAO_LABEL[s.doadora.situacao] || s.doadora.situacao}
                                          style={SITUACAO_STYLE[s.doadora.situacao] || SITUACAO_STYLE.NORMAL}
                                        />
                                      </div>
                                    </div>

                                    {/* Deficitária */}
                                    <div>
                                      <div style={{ fontWeight:600, color:"#2c3e50", fontSize:12 }}>{s.deficitaria.fantasia}</div>
                                      <div style={{ fontSize:10.5, color:"#7f8c8d", marginTop:2 }}>
                                        Saldo: <strong>{fmtInt(s.deficitaria.saldo)}</strong>
                                        {" · "}
                                        {s.deficitaria.cobertura_dias >= 9999 ? "∞" : `${fmtInt(Math.max(0, s.deficitaria.cobertura_dias))} dias`} cobertura
                                      </div>
                                      <div style={{ marginTop:3 }}>
                                        <Badge
                                          text={SITUACAO_LABEL[s.deficitaria.situacao] || s.deficitaria.situacao}
                                          style={SITUACAO_STYLE[s.deficitaria.situacao] || SITUACAO_STYLE.NORMAL}
                                        />
                                      </div>
                                    </div>

                                    {/* Qtd sugerida */}
                                    <div style={{ textAlign:"right" }}>
                                      <span style={{
                                        fontSize:20, fontWeight:800, color:cfg.cor, lineHeight:1,
                                      }}>
                                        {fmtInt(s.qtd_sugerida)}
                                      </span>
                                      <div style={{ fontSize:10, color:"#b2bec3" }}>unidades</div>
                                    </div>

                                    {/* Valor estimado */}
                                    <div style={{ textAlign:"right" }}>
                                      {s.valor_estimado > 0 ? (
                                        <>
                                          <div style={{ fontSize:13, fontWeight:700, color:"#2c3e50" }}>
                                            R$ {fmtNum(s.valor_estimado)}
                                          </div>
                                          <div style={{ fontSize:10, color:"#b2bec3" }}>estimado</div>
                                        </>
                                      ) : (
                                        <span style={{ color:"#b2bec3" }}>—</span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}

                        {/* Rodapé explicativo */}
                        <div style={{ fontSize:11, color:"#9ca3af", marginTop:8, lineHeight:1.6 }}>
                          <strong>Como foi calculado:</strong> A quantidade sugerida é o mínimo necessário para a loja deficitária atingir 30 dias de cobertura,
                          limitado ao que a loja doadora pode ceder sem ficar abaixo de 30 dias.
                          O valor estimado usa o preço médio de venda do período.
                          Produtos Classe D são tratados como doadores sem restrição de estoque de segurança.
                        </div>

                      </div>
                    );
                  })()}
                </TabPane>

              </TabContent>
              </div>{/* fim overlay wrapper */}
            </CardBody>
          </Card>
        )}

        {/* Estado inicial / sem resultado */}
        {!loadingTabela && listaCurva.length === 0 && !resumo && (
          <Card body className="text-center py-5">
            <i
              className="fa fa-bar-chart"
              style={{ fontSize: 40, color: "#dfe6e9", marginBottom: 10 }}
            />
            <p style={{ color: "#7f8c8d", margin: 0 }}>
              Selecione as lojas, o período e clique em{" "}
              <strong>Pesquisar</strong> para gerar a análise.
            </p>
          </Card>
        )}

        {/* ════ POR PRODUTO ════ */}
        {!loading && (
          <Card>
            <CardHeader>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <CardTitle tag="h6" className="mb-0" style={{ margin: 0, fontWeight: 700 }}>
                  <i className="fa fa-cube mr-2" style={{ color: "#e74c3c" }} />
                  Produto × Lojas
                </CardTitle>
                <span style={{ fontSize: 12, color: "#7f8c8d" }}>
                  Analise um produto individualmente em todas as lojas
                </span>
              </div>
            </CardHeader>
            <CardBody>
              <Row className="align-items-end" style={{ marginBottom: 4 }}>
                <Col md="6" className="mb-2">
                  <Label style={{ fontSize: 11, fontWeight: 600, color: "#7f8c8d", marginBottom: 3 }}>
                    Produto
                  </Label>
                  <AsyncSelect
                    cacheOptions
                    loadOptions={buscaProdutos}
                    placeholder="Digite o nome ou cód. de barras..."
                    noOptionsMessage={({ inputValue }) =>
                      inputValue.length < 3 ? "Digite ao menos 3 caracteres" : "Nenhum produto encontrado"
                    }
                    onChange={(opt) => setProdutoPorLojaSelect(opt)}
                    value={produtoPorLojaSelect}
                    isClearable
                  />
                </Col>
                <Col md="2" className="mb-2">
                  <Label style={{ fontSize: 11, fontWeight: 600, color: "#7f8c8d", marginBottom: 3 }}>
                    Início
                  </Label>
                  <DateTime
                    timeFormat={false}
                    dateFormat="DD/MM/YYYY"
                    closeOnSelect
                    inputProps={{ placeholder: "dd/mm/aaaa", className: "form-control", style: { height: 38 } }}
                    value={formPorProduto.inicio}
                    onChange={(v) => setFormPorProduto((f) => ({ ...f, inicio: typeof v === "string" ? v : v.format("DD/MM/YYYY") }))}
                  />
                </Col>
                <Col md="2" className="mb-2">
                  <Label style={{ fontSize: 11, fontWeight: 600, color: "#7f8c8d", marginBottom: 3 }}>
                    Fim
                  </Label>
                  <DateTime
                    timeFormat={false}
                    dateFormat="DD/MM/YYYY"
                    closeOnSelect
                    inputProps={{ placeholder: "dd/mm/aaaa", className: "form-control", style: { height: 38 } }}
                    value={formPorProduto.final}
                    onChange={(v) => setFormPorProduto((f) => ({ ...f, final: typeof v === "string" ? v : v.format("DD/MM/YYYY") }))}
                  />
                </Col>
                <Col md="2" className="mb-0">
                  <Button
                    color="danger"
                    block
                    onClick={pesquisarPorProduto}
                    disabled={loadingPorLoja}
                    style={{ height: 38 }}
                  >
                    {loadingPorLoja
                      ? <><i className="fa fa-spinner fa-spin" /> Buscando...</>
                      : <><i className="fa fa-search" /> Analisar</>
                    }
                  </Button>
                </Col>
              </Row>

              {/* Cards por loja */}
              {dadosPorLoja.length > 0 && (() => {
                const produto = dadosPorLoja[0];
                return (
                  <>
                    <div style={{ marginBottom: 14 }}>
                      <span style={{ fontWeight: 700, fontSize: 15, color: "#2c3e50" }}>
                        {produto.produto}
                      </span>
                      {produto.cod_barras && (
                        <span style={{ marginLeft: 10, fontSize: 12, color: "#7f8c8d" }}>
                          Cód: {produto.cod_barras}
                        </span>
                      )}
                    </div>
                    <Row>
                      {dadosPorLoja.map((item, idx) => {
                        const cls = item.curva_classe;
                        const sit = item.situacao;
                        return (
                          <Col key={`pp-${idx}`} md="4" lg="3" className="mb-3">
                            <div style={{
                              border: "1px solid #e5e7eb",
                              borderRadius: 10,
                              padding: "14px 16px",
                              height: "100%",
                              background: "#fff",
                              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                            }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                                <div>
                                  <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>{item.fantasia}</div>
                                  <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 1 }}>{fmtCnpj(item.cnpj)}</div>
                                </div>
                                <Badge
                                  text={`Classe ${cls}`}
                                  style={CLASSE_STYLE[cls] || CLASSE_STYLE.D}
                                />
                              </div>
                              <div style={{ marginBottom: 10 }}>
                                <Badge
                                  text={SITUACAO_LABEL[sit] || sit}
                                  style={SITUACAO_STYLE[sit] || SITUACAO_STYLE.NORMAL}
                                />
                              </div>
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 12px", fontSize: 12 }}>
                                <div>
                                  <div style={{ color: "#94a3b8", fontSize: 10.5, marginBottom: 1 }}>Estoque</div>
                                  <div style={{ fontWeight: 700, color: "#1e293b" }}>{fmtInt(item.prod_saldo)} un</div>
                                </div>
                                <div>
                                  <div style={{ color: "#94a3b8", fontSize: 10.5, marginBottom: 1 }}>Cobertura</div>
                                  <div style={{ fontWeight: 700, color: "#1e293b" }}>{coberturaTxt(item.cobertura_dias)}</div>
                                </div>
                                <div>
                                  <div style={{ color: "#94a3b8", fontSize: 10.5, marginBottom: 1 }}>Qt vendida</div>
                                  <div style={{ fontWeight: 700, color: "#1e293b" }}>{fmtInt(item.curva_qtvendida)} un</div>
                                </div>
                                <div>
                                  <div style={{ color: "#94a3b8", fontSize: 10.5, marginBottom: 1 }}>Total vendas</div>
                                  <div style={{ fontWeight: 700, color: "#1e293b" }}>R$ {fmtNum(item.curva_totalvenda)}</div>
                                </div>
                                <div>
                                  <div style={{ color: "#94a3b8", fontSize: 10.5, marginBottom: 1 }}>Val. estoque</div>
                                  <div style={{ fontWeight: 700, color: "#1e293b" }}>R$ {fmtNum(item.estoque_valor)}</div>
                                </div>
                                <div>
                                  <div style={{ color: "#94a3b8", fontSize: 10.5, marginBottom: 1 }}>Últ. venda</div>
                                  <div style={{ fontWeight: 700, color: item.data_ult_venda ? "#1e293b" : "#94a3b8" }}>
                                    {item.data_ult_venda || "—"}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </>
                );
              })()}

              {!loadingPorLoja && dadosPorLoja.length === 0 && produtoPorLojaSelect && (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#7f8c8d", fontSize: 13 }}>
                  <i className="fa fa-inbox" style={{ fontSize: 28, display: "block", marginBottom: 8 }} />
                  Nenhum dado encontrado para esse produto no período selecionado.
                </div>
              )}

              {!loadingPorLoja && dadosPorLoja.length === 0 && !produtoPorLojaSelect && (
                <div style={{ textAlign: "center", padding: "30px 0 10px", color: "#b2bec3", fontSize: 13 }}>
                  <i className="fa fa-cube" style={{ fontSize: 28, display: "block", marginBottom: 8 }} />
                  Selecione um produto e clique em <strong>Analisar</strong> para ver como ele se comporta em cada loja.
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {/* ════ MODAL EXPORTAR ════ */}
        <Modal isOpen={modalExportar} toggle={() => setModalExportar(false)} size="md">
          <ModalHeader
            toggle={() => setModalExportar(false)}
            style={{ background: "#f8f9fa", borderBottom: "2px solid #e74c3c" }}
          >
            <i className="fa fa-download mr-2" style={{ color: "#e74c3c" }} />
            Exportar / Imprimir
          </ModalHeader>

          <ModalBody style={{ padding: "20px 24px" }}>

            {/* ── Consolidado ── */}
            <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: "12px 16px", marginBottom: 10 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 0, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                <input type="checkbox" checked={opcoesExportar.consolidado}
                  onChange={(e) => setOpcoesExportar({ ...opcoesExportar, consolidado: e.target.checked })} />
                <i className="fa fa-list" style={{ color: "#e74c3c" }} /> Consolidado
              </label>
              {opcoesExportar.consolidado && (
                <div style={{ marginLeft: 22, marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {["A","B","C","D"].map((cl) => (
                    <label key={cl} style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", marginBottom: 0 }}>
                      <input type="checkbox" checked={opcoesExportar[`classe${cl}`]}
                        onChange={(e) => setOpcoesExportar({ ...opcoesExportar, [`classe${cl}`]: e.target.checked })} />
                      <span style={{
                        background: CLASSE_STYLE[cl].bg, color: CLASSE_STYLE[cl].color,
                        border: CLASSE_STYLE[cl].border,
                        padding: "2px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 700,
                      }}>
                        Classe {cl}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* ── Por Loja ── */}
            <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: "12px 16px", marginBottom: 10 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 0, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                <input type="checkbox" checked={opcoesExportar.porLoja}
                  onChange={(e) => setOpcoesExportar({ ...opcoesExportar, porLoja: e.target.checked })} />
                <i className="fa fa-building" style={{ color: "#3498db" }} /> Por Loja
                <span style={{ fontWeight: 400, fontSize: 11.5, color: "#7f8c8d" }}>— resumo por filial</span>
              </label>
            </div>

            {/* ── Transferências (só se > 1 loja) ── */}
            {lojasNaLista.length > 1 && (
              <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: "12px 16px", marginBottom: 10 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 0, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                  <input type="checkbox" checked={opcoesExportar.transferencias}
                    onChange={(e) => setOpcoesExportar({ ...opcoesExportar, transferencias: e.target.checked })} />
                  <i className="fa fa-exchange" style={{ color: "#9a3412" }} /> Transferências
                  {sugestoesTransferencia.length > 0 && (
                    <span style={{ fontWeight: 400, fontSize: 11.5, color: "#7f8c8d" }}>
                      — {sugestoesTransferencia.length} sugestões disponíveis
                    </span>
                  )}
                </label>
                {opcoesExportar.transferencias && (
                  <div style={{ marginLeft: 22, marginTop: 10, display: "flex", gap: 16 }}>
                    {[
                      { key: "priAlta",  label: "Alta",  color: "#b91c1c" },
                      { key: "priMedia", label: "Média", color: "#9a3412" },
                      { key: "priBaixa", label: "Baixa", color: "#713f12" },
                    ].map((p) => (
                      <label key={p.key} style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", marginBottom: 0 }}>
                        <input type="checkbox" checked={opcoesExportar[p.key]}
                          onChange={(e) => setOpcoesExportar({ ...opcoesExportar, [p.key]: e.target.checked })} />
                        <span style={{ fontWeight: 700, color: p.color, fontSize: 12 }}>{p.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            <hr style={{ margin: "14px 0" }} />

            {/* ── Formato ── */}
            <div style={{ fontWeight: 700, marginBottom: 10, fontSize: 13 }}>Formato</div>
            <div style={{ display: "flex", gap: 10 }}>
              {[
                { value: "pdf",   label: "PDF",         icon: "fa-file-pdf-o",   color: "#DC2626" },
                { value: "excel", label: "Excel (CSV)",  icon: "fa-file-excel-o", color: "#27ae60" },
                { value: "cmv",   label: "CMV",          icon: "fa-table",        color: "#0369a1" },
              ].map((f) => (
                <label
                  key={f.value}
                  onClick={() => setOpcoesExportar({ ...opcoesExportar, formato: f.value })}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    gap: 7, cursor: "pointer", flex: 1,
                    padding: "10px 14px", borderRadius: 8,
                    border: `2px solid ${opcoesExportar.formato === f.value ? f.color : "#e5e7eb"}`,
                    background: opcoesExportar.formato === f.value ? f.color + "15" : "#fff",
                    marginBottom: 0, transition: "all 150ms",
                  }}
                >
                  <i className={`fa ${f.icon}`} style={{ color: f.color, fontSize: 18 }} />
                  <span style={{ fontWeight: 600, color: f.color, fontSize: 13 }}>{f.label}</span>
                </label>
              ))}
            </div>

            {opcoesExportar.formato === "cmv" && (
              <div style={{ marginTop: 10, padding: "8px 12px", background: "#f0f9ff", border: "1px solid #BAE6FD", borderRadius: 6, fontSize: 11.5, color: "#0369a1" }}>
                <i className="fa fa-info-circle mr-1" />
                Exporta: cod_barras, produto, fornecedor, categoria, loja, custo atual, valor última compra, saldo, classe — para importação de CMV.
              </div>
            )}

          </ModalBody>

          <ModalFooter style={{ background: "#f8f9fa" }}>
            <Button color="secondary" outline onClick={() => setModalExportar(false)}>Cancelar</Button>
            <Button
              color="danger"
              onClick={gerarExportacao}
              disabled={!opcoesExportar.consolidado && !opcoesExportar.porLoja && !opcoesExportar.transferencias}
            >
              <i className="fa fa-download mr-1" /> Gerar
            </Button>
          </ModalFooter>
        </Modal>

      </Container>
    </div>
  );
};

export default CurvaABCD;
